import ExpirationDateField from "./ExpirationDateField";
import { expirationDatePayload } from "../../utils/expirationDate";
import DeliveryNoteCorrectionControl, { DeliveryNotePendingCorrection, DeliveryNoteQuantityDetail, receivedInventoryQuantities, correctionUnitSource, validateCorrectionLines } from "./DeliveryNoteCorrections";
import { deliveryNoteCorrectionText, deliveryNoteCorrectionError } from "./deliveryNoteCorrectionMessages";
import DeliveryNoteAdjustments from "./DeliveryNoteAdjustments";
import { prepareDeliveryNoteAdjustments } from "../../utils/deliveryNoteAdjustments";
import { itemMatchesSupplier, findItemSupplierRelation } from "../../utils/supplierIdentity";
import { useReceivingDestinations, deliveryNoteLineLocation } from "./ReceivingDestinations";
import DeliveryNoteLocationPicker from "./DeliveryNoteLocationPicker";
import { isUnavailableMainLocation } from "../../utils/receivingLocationPolicy";
import TenderDeliveryNoteWarning from "./TenderPriceFeedback";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import {
  buildDeliveryNoteUnitOptions,
  deliveryNoteInventoryValues,
  formatUnitNumber,
  mergeDeliveryNoteUnitSource,
  refreshDeliveryNoteLineUnits,
  unitHierarchyLabel,
} from "../../utils/deliveryNoteUnits";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import OriginalDocumentLayout, { useOriginalDocument } from "../originals/OriginalDocumentLayout";
import { ocrDistributorTranslator } from "./ocrDistributorMessages";
import { getUnitName } from "../../utils/unitNames";
import { useComplimentaryConfirmation } from "./ComplimentaryStockConfirmation";
import { usePurchaseQuantityReview } from "./PurchaseQuantityReview";
import ComplimentaryPriceField from "./ComplimentaryPriceField";
import { fetchDeliveryNoteReferencePrice } from "../../utils/deliveryNoteReferencePrice";
import { isDeliveryNotePriceValidForVerification, isExplicitZeroPrice, normalizeComplimentaryPrice, updateComplimentaryLine } from "../../utils/complimentaryStock";
import { readStoredUser } from "../../../utils/sessionUser";
import { deliveryNoteDuplicateError, deliveryNoteNumberError, deliveryNoteLifecycleText } from "./deliveryNoteLifecycleMessages";
import "../../PurchasingUi.css";
import "./PurchasingReceivingPopups.css";

const styles = `
  .edn-wrap{position:fixed;inset:0;z-index:9999;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px}
  .edn-modal{width:min(1420px,97vw);max-height:calc(100vh - 24px);overflow:auto;border-radius:22px;border:1px solid var(--pu-border);background:var(--pu-surface);color:var(--pu-text);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .edn-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:16px 20px 12px;border-bottom:1px solid var(--pu-border)}
  .edn-title{margin:0;font-size:clamp(1.06rem,1.45vw,1.38rem);font-weight:900;line-height:1.05}
  .edn-sub{margin-top:6px;font-size:.8rem;color:var(--pu-muted)}
  .edn-headActions{display:flex;align-items:center;gap:10px;flex-wrap:wrap;justify-content:flex-end}
  .edn-pill{padding:6px 11px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.72rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase}
  .edn-pill--draft{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .edn-pill--verified{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .edn-pill--differences{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .edn-pill--cancelled{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .edn-close,.edn-btn{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .edn-close{min-width:40px;height:40px;padding:0 12px;font-weight:800}
  .edn-btn{padding:9px 13px;font-size:.86rem;font-weight:750}
  .edn-btn--primary{border-color:var(--pu-primary);background:var(--pu-primary);color:#ffffff}
  .edn-btn--cyan{border-color:var(--pu-border);color:var(--pu-text);background:var(--pu-soft)}
  .edn-btn--success{border-color:var(--pu-success);background:var(--pu-success-bg);color:var(--pu-success)}
  .edn-btn--warning{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .edn-btn--original{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .edn-btn--danger{border-color:var(--pu-danger);background:var(--pu-danger-bg);color:var(--pu-danger)}
  .edn-btn--soft{border-color:var(--pu-border);background:var(--pu-soft)}
  .edn-body{padding:12px 20px 20px}
  .edn-banner{margin-bottom:16px;border-radius:14px;padding:12px 14px;font-size:.88rem;line-height:1.45}
  .edn-wrap .edn-banner--error{border:1px solid var(--pu-border);background:var(--pu-danger-bg);color:var(--pu-danger);white-space:pre-line;overflow-wrap:anywhere}
  .edn-banner--success{border:1px solid var(--pu-border);background:var(--pu-success-bg);color:var(--pu-success)}
  .edn-banner--warning{border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .edn-grid{display:grid;gap:16px}
  .edn-grid--top{grid-template-columns:minmax(0,1fr) minmax(260px,.46fr);align-items:start}
  .edn-grid--four{grid-template-columns:repeat(4,minmax(0,1fr))}
  .edn-grid--two{grid-template-columns:repeat(2,minmax(0,1fr))}
  .edn-section,.edn-summary,.edn-totalBlock,.edn-card,.edn-expense,.edn-stat{border:1px solid var(--pu-border);border-radius:16px;padding:12px 14px;background:var(--pu-soft)}
  .edn-label{display:block;margin-bottom:6px;color:var(--pu-muted);font-size:.72rem;text-transform:uppercase;font-weight:700;letter-spacing:.05em}
  .edn-value{display:block;color:var(--pu-text);font-size:.84rem;font-weight:800;line-height:1.25;overflow-wrap:anywhere}
  .edn-summary h6,.edn-sectionHead h6,.edn-expenseHead h6{margin:0;font-size:.94rem;font-weight:850}
  .edn-summary p,.edn-sectionHead p,.edn-expenseHead p{margin:5px 0 0;color:var(--pu-text);font-size:.76rem;line-height:1.35}
  .edn-stats{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;margin-top:10px}
  .edn-stat span,.edn-totalBlock span{display:block;color:var(--pu-text);font-size:.66rem;text-transform:uppercase;font-weight:700;letter-spacing:.06em}
  .edn-stat strong,.edn-totalBlock strong{display:block;margin-top:4px;font-weight:800;color:var(--pu-text)}
  .edn-totalBlock strong{font-size:1.22rem;color:var(--pu-text)}
  .edn-sectionHead,.edn-expenseHead,.edn-footer{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
  .edn-footer{align-items:center;flex-wrap:wrap}
  .edn-footer>.edn-footerActions{flex:1 1 auto}
  .edn-sectionHead{margin-bottom:12px}
  .edn-actions,.edn-footerActions,.edn-expenseActions{display:flex;gap:10px;flex-wrap:wrap;justify-content:flex-end}
  .edn-empty{border:1px dashed var(--pu-border);border-radius:20px;padding:24px 22px;background:var(--pu-soft)}
  .edn-empty strong{display:block;font-size:1rem}
  .edn-empty span{display:block;margin-top:6px;color:var(--pu-text);font-size:.86rem;line-height:1.45}
  .edn-modal .form-control,.edn-modal .form-select{width:100%;padding:8px 10px;min-height:38px;border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-surface)!important;color:var(--pu-text)!important}
  .edn-modal .form-control.edn-noteNumber{text-align:center}
  .edn-modal .form-select option{background:var(--pu-surface);color:var(--pu-text)}
  .edn-modal input[type=number],.edn-receive input[type=number]{appearance:textfield;-moz-appearance:textfield}
  .edn-modal input[type=number]::-webkit-outer-spin-button,.edn-modal input[type=number]::-webkit-inner-spin-button,.edn-receive input[type=number]::-webkit-outer-spin-button,.edn-receive input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;appearance:none;margin:0}
  .edn-tableShell{border:1px solid var(--pu-border);border-radius:18px;padding:10px;background:var(--pu-soft)}
  .edn-tableWrap{overflow:auto;border:1px solid var(--pu-border);border-radius:16px;background:var(--pu-soft)}
  .edn-table{--edn-control-height:37px;width:100%;min-width:0;border-collapse:separate;border-spacing:0;table-layout:fixed;font-size:.8rem}
  .edn-table--draft{min-width:0}
  .edn-col--item{width:22%}
  .edn-col--unit{width:16%}
  .edn-col--received{width:9%}
  .edn-col--qtyNote{width:6%}
  .edn-col--expiration{width:12%}
  .edn-col--priceNote{width:11%}
  .edn-col--pending{width:11%}
  .edn-col--total{width:9%}
  .edn-col--actions{width:4%}
  .edn-table--without-received .edn-col--item{width:28%}
  .edn-table--without-received .edn-col--unit{width:19%}
  .edn-table th,.edn-table td{padding:8px 10px;border-bottom:1px solid var(--pu-border);vertical-align:middle}
  .edn-table > tbody > tr > td{vertical-align:top}
  .edn-table th{position:sticky;top:0;z-index:2;background:var(--pu-primary);color:#ffffff;font-size:.66rem;text-transform:uppercase;letter-spacing:.04em;font-weight:800}
  .edn-table th:first-child{border-top-left-radius:14px}
  .edn-table th:last-child{border-top-right-radius:14px}
  .edn-table tbody tr{background:var(--pu-surface)}
  .edn-table tbody tr:nth-child(even){background:var(--pu-soft)}
  .edn-table tbody tr:last-child td{border-bottom:none}
  .edn-table .form-control{min-height:34px;padding:6px 8px;font-size:.82rem}
  .edn-table .form-control.edn-noteNumber{height:var(--edn-control-height);min-height:var(--edn-control-height)}
  .edn-itemCell{min-width:0;overflow:hidden;text-align:left}
  .edn-itemTitle{max-width:100%;font-weight:850;color:var(--pu-text);line-height:1.3;white-space:normal;overflow-wrap:anywhere;word-break:normal}
  .edn-itemMeta{margin-top:4px;color:var(--pu-muted);font-size:.72rem;line-height:1.3;overflow-wrap:anywhere}
  .edn-unitCell{min-width:0;text-align:left;color:var(--pu-text);font-size:.78rem;line-height:1.25}
  .edn-unitPicker{display:flex;align-items:center;gap:3px;width:100%;padding:3px;border:1px solid var(--pu-border);border-radius:10px;background:var(--pu-soft)}
  .edn-unitChoice{min-width:0;flex:1 1 0;height:29px;padding:0 7px;border:1px solid var(--pu-border);border-radius:7px;background:transparent;color:var(--pu-text);font-size:.72rem;font-weight:800;line-height:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .edn-unitChoice:hover:not(:disabled){color:var(--pu-text);background:var(--pu-soft)}
  .edn-unitChoice--active{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text);box-shadow:none}
  .edn-unitChoice:disabled{cursor:default;opacity:.78}
  .edn-unitMetaBox{min-width:0;margin-top:6px;padding:6px 8px;border:1px solid var(--pu-border);border-radius:9px;background:var(--pu-soft)}
  .edn-unitRelation{display:block;color:var(--pu-muted);font-size:.65rem;font-weight:650;line-height:1.3;white-space:normal;overflow-wrap:anywhere}
  .edn-unitPreview{display:block;color:var(--pu-text);font-size:.67rem;font-weight:750;line-height:1.3;white-space:normal;overflow-wrap:anywhere}
  .edn-unitRelation+.edn-unitPreview{margin-top:3px}
  .edn-readonly{color:var(--pu-text);font-weight:700;text-align:center}
  .edn-total{text-align:right;font-weight:850;color:var(--pu-text)}
  .edn-diff{display:inline-flex;align-items:center;padding:5px 9px;border-radius:999px;border:1px solid var(--pu-border);font-size:.7rem;font-weight:800;text-transform:uppercase;letter-spacing:.05em}
  .edn-receivedCheck{display:inline-block;width:14px;height:14px;margin-left:6px;vertical-align:-2px;color:var(--pu-success)}
  .edn-diff--warn{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .edn-mobile{display:grid;gap:12px}
  .edn-cardTop{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}
  .edn-cardActions{display:flex;align-items:center;justify-content:flex-end;gap:8px;flex-wrap:wrap}
  .edn-cardTitle{font-size:.96rem;font-weight:850;color:var(--pu-text);line-height:1.2}
  .edn-cardSub{margin-top:4px;color:var(--pu-muted);font-size:.78rem;line-height:1.4}
  .edn-mobileGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:12px}
  .edn-expenses{display:grid;gap:12px}
  .edn-expense--distributed{border-color:var(--pu-border);background:var(--pu-soft)}
  .edn-expenseAmount{padding:8px 12px;border-radius:999px;background:var(--pu-soft);border:1px solid var(--pu-border);color:var(--pu-text);font-size:.82rem;font-weight:850}
  .edn-note{margin-top:12px;border-radius:16px;background:var(--pu-soft);border:1px solid var(--pu-border);padding:12px;color:var(--pu-muted);font-size:.82rem;line-height:1.45}
  .edn-poStatus{position:relative;display:flex;min-height:76px;align-items:center;justify-content:center;padding:18px 4px 0}
  .edn-poText{position:absolute;top:0;right:0;color:var(--pu-text);font-size:.72rem;font-style:italic;font-weight:750;line-height:1;white-space:nowrap}
  .edn-poText--pending{color:var(--pu-text)}
  .edn-poText--linked{color:var(--pu-text)}
  .edn-poStatus .edn-btn{min-width:108px;justify-content:center}
  .edn-pickerWrap{position:fixed;inset:0;z-index:10060;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:18px}
  .edn-picker{width:min(760px,calc(100vw - 32px));max-height:calc(100vh - 36px);display:flex;flex-direction:column;overflow:hidden;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-soft);box-shadow:none}
  .edn-pickerHead{display:flex;justify-content:space-between;gap:14px;align-items:flex-start;padding:18px 20px 12px;border-bottom:1px solid var(--pu-border)}
  .edn-pickerHead h6{margin:0;font-size:1rem;font-weight:850}
  .edn-pickerHead p{margin:6px 0 0;color:var(--pu-text);font-size:.82rem;line-height:1.45}
  .edn-pickerBody{display:flex;flex-direction:column;min-height:0;padding:14px 20px 18px}
  .edn-pickerList{display:grid;gap:8px;min-height:0;overflow:auto;margin-top:12px}
  .edn-pickerItem{width:100%;padding:11px 13px;border:1px solid var(--pu-border);border-radius:14px;background:var(--pu-soft);color:var(--pu-text);text-align:left}
  .edn-pickerItem:hover{border-color:var(--pu-border);background:var(--pu-soft)}
  .edn-pickerItem strong{display:block;font-size:.88rem;font-weight:850;line-height:1.35}
  .edn-pickerItem span{display:block;margin-top:4px;color:var(--pu-text);font-size:.74rem;line-height:1.4}
  .edn-pickerTag{display:inline-block!important;width:max-content;margin-top:7px!important;padding:3px 7px;border:1px solid var(--pu-border);border-radius:999px;background:var(--pu-soft);color:var(--pu-text)!important;font-size:.62rem!important;font-weight:800;text-transform:uppercase}
  .edn-receiveWrap{position:fixed;inset:0;z-index:10050;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:24px}
  .edn-receive{width:min(1120px,calc(100vw - 32px));max-height:calc(100vh - 32px);display:flex;flex-direction:column;overflow:hidden;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .edn-receiveHead{flex:0 0 auto;display:flex;justify-content:space-between;gap:14px;align-items:flex-start;padding:18px 20px 12px;border-bottom:1px solid var(--pu-border)}
  .edn-receiveHead h6{margin:0;font-size:1rem;font-weight:850}
  .edn-receiveHead p{margin:6px 0 0;color:var(--pu-text);font-size:.82rem;line-height:1.45}
  .edn-receiveBody{display:flex;flex-direction:column;gap:14px;min-height:0;overflow:hidden;padding:14px 20px 18px}
  .edn-receiveTableWrap{min-height:0;overflow:auto;border:1px solid var(--pu-border);border-radius:18px;background:var(--pu-soft)}
  .edn-receiveTable{width:100%;min-width:780px;border-collapse:separate;border-spacing:0}
  .edn-receiveTable th,.edn-receiveTable td{padding:10px 11px;border-bottom:1px solid var(--pu-border);vertical-align:middle}
  .edn-receiveTable th:nth-child(2),.edn-receiveTable td:nth-child(2),.edn-receiveTable th:nth-child(3),.edn-receiveTable td:nth-child(3),.edn-receiveTable th:nth-child(4),.edn-receiveTable td:nth-child(4){width:112px}
  .edn-receiveTable th:nth-child(5),.edn-receiveTable td:nth-child(5){width:132px}
  .edn-receiveTable th{color:#ffffff;font-size:.7rem;text-transform:uppercase;letter-spacing:.06em}
  .edn-receiveInput{max-width:104px;margin:0 auto}
  .edn-receiveMeta{color:var(--pu-muted);font-size:.76rem;line-height:1.35}
  .edn-receiveFooter{flex:0 0 auto;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
  .edn-receiveTotal{color:var(--pu-text);font-size:.86rem;font-weight:850}
  .edn-confirmWrap{position:fixed;inset:0;z-index:10040;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:18px}
  .edn-confirm{width:min(680px,94vw);max-height:calc(100vh - 36px);overflow:auto;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .edn-confirmHead{padding:18px 18px 12px;border-bottom:1px solid var(--pu-border)}
  .edn-confirmHead h6{margin:0;font-size:1rem;font-weight:850}
  .edn-confirmBody{padding:14px 18px 18px}
  .edn-confirmBody p{margin:0;color:var(--pu-text);font-size:.92rem;line-height:1.5}
  .edn-cancelLines{display:grid;gap:12px;margin-top:14px}
  .edn-cancelLocations{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:6px;margin-top:8px}
  .edn-cancelLocations .edn-pickerItem[aria-pressed=true]{border-color:var(--pu-primary);box-shadow:inset 0 0 0 1px var(--pu-primary)}
  .edn-impactWrap{position:fixed;inset:0;z-index:10070;background:var(--pu-soft);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:18px}
  .edn-impact{width:min(780px,96vw);max-height:calc(100vh - 36px);overflow:auto;border-radius:22px;border:1px solid var(--pu-border);background:var(--pu-soft);box-shadow:none;color:var(--pu-text)}
  .edn-impactHead{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:18px 20px 14px;border-bottom:1px solid var(--pu-border)}
  .edn-impactHead h6{margin:0;font-size:1.08rem;font-weight:900}
  .edn-impactHead p{margin:6px 0 0;color:var(--pu-text);font-size:.84rem;line-height:1.45}
  .edn-impactFlag{flex:0 0 auto;padding:6px 10px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.68rem;font-weight:850;text-transform:uppercase;letter-spacing:.05em}
  .edn-impactBody{padding:16px 20px 20px}
  .edn-impactStats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
  .edn-impactStats--three{grid-template-columns:repeat(3,minmax(0,1fr))}
  .edn-impactStat{min-width:0;padding:11px 12px;border:1px solid var(--pu-border);border-radius:12px;background:var(--pu-soft)}
  .edn-impactStat span{display:block;color:var(--pu-text);font-size:.65rem;font-weight:750;text-transform:uppercase;letter-spacing:.05em}
  .edn-impactStat strong{display:block;margin-top:5px;color:var(--pu-text);font-size:.94rem;font-weight:850;overflow-wrap:anywhere}
  .edn-impactExplanation{margin-top:12px;padding:13px 14px;border:1px solid var(--pu-border);border-radius:14px;background:var(--pu-soft)}
  .edn-impactExplanation strong{display:block;color:var(--pu-text);font-size:.86rem;font-weight:850}
  .edn-impactExplanation ol{margin:8px 0 0;padding-left:19px;color:var(--pu-text);font-size:.82rem;line-height:1.5}
  .edn-impactExplanation li+li{margin-top:5px}
  .edn-impactGrid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px;margin-top:12px}
  .edn-impactPanel{min-width:0;border:1px solid var(--pu-border);border-radius:14px;background:var(--pu-soft);overflow:hidden}
  .edn-impactPanelHead{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 12px;border-bottom:1px solid var(--pu-border)}
  .edn-impactPanelHead strong{font-size:.8rem;font-weight:850}
  .edn-impactPanelHead span{color:var(--pu-text);font-size:.7rem;font-weight:750}
  .edn-impactList{display:grid;max-height:190px;overflow:auto}
  .edn-impactRow{padding:10px 12px;border-bottom:1px solid var(--pu-border)}
  .edn-impactRow:last-child{border-bottom:none}
  .edn-impactRow strong{display:block;color:var(--pu-text);font-size:.8rem;font-weight:800;line-height:1.35;overflow-wrap:anywhere}
  .edn-impactRow span{display:block;margin-top:3px;color:var(--pu-text);font-size:.7rem;line-height:1.35;overflow-wrap:anywhere}
  .edn-impactChanges{display:grid;gap:10px;margin-top:12px}
  .edn-impactChange{padding:12px 13px;border:1px solid var(--pu-border);border-radius:14px;background:var(--pu-soft)}
  .edn-impactChangeHead{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
  .edn-impactChangeHead strong{display:block;color:var(--pu-text);font-size:.84rem;font-weight:850;line-height:1.35}
  .edn-impactChangeHead span{display:block;margin-top:3px;color:var(--pu-text);font-size:.7rem}
  .edn-impactMode{flex:0 0 auto;margin:0!important;padding:5px 8px;border:1px solid var(--pu-border);border-radius:999px;background:var(--pu-soft);color:var(--pu-text)!important;font-size:.64rem!important;font-weight:800;text-transform:uppercase}
  .edn-impactDeltaGrid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:10px}
  .edn-impactDelta{padding:8px 9px;border:1px solid var(--pu-border);border-radius:10px;background:var(--pu-soft)}
  .edn-impactDelta span{display:block;color:var(--pu-text);font-size:.62rem;font-weight:750;text-transform:uppercase}
  .edn-impactDelta strong{display:block;margin-top:4px;color:var(--pu-text);font-size:.76rem;font-weight:800;overflow-wrap:anywhere}
  .edn-impactEffect{margin-top:9px;color:var(--pu-text);font-size:.76rem;line-height:1.45}
  .edn-impactNote{margin-top:12px;padding:11px 12px;border:1px solid var(--pu-border);border-radius:12px;background:var(--pu-soft);color:var(--pu-muted);font-size:.8rem;line-height:1.45}
  .edn-impactActions{display:flex;justify-content:flex-end;gap:10px;margin-top:16px}
  .edn-grid--top>.edn-summary{contain:size;align-self:stretch;min-height:0;overflow:auto;padding:12px 14px}
  .edn-summary .edn-stats{gap:8px 16px;margin-top:10px}
  .edn-summary .edn-stat{display:flex;align-items:baseline;justify-content:space-between;gap:8px;min-width:0;padding:0;border:0;border-radius:0;background:none}
  .edn-summary .edn-stat span{font-size:.66rem;letter-spacing:0;text-transform:none;overflow-wrap:anywhere}
  .edn-summary .edn-stat strong{margin:0;font-size:.92rem}
  .edn-locationTrigger{text-align:left;overflow-wrap:anywhere}
  .edn-lineLocation{display:block;max-width:100%;margin-top:4px;color:var(--pu-muted);font-size:11px;line-height:1.35;overflow-wrap:anywhere}
  .edn-lineLocation--editable{padding:0;border:0;background:transparent;text-align:left;text-decoration:underline dotted;text-underline-offset:3px;cursor:pointer}
  .edn-lineLocation--editable:hover{color:var(--pu-primary)}
  .edn-lineLocation--editable:focus-visible{outline:2px solid var(--pu-accent);outline-offset:3px}
  .edn-modal .edn-tableShell{padding:0;min-width:0;border:0}
  .edn-modal .edn-tableWrap{min-width:0}
  .purchasing-receiving-ui .edn-modal .edn-table :is(th,td){padding:8px 6px;overflow-wrap:anywhere;white-space:normal}
  .edn-table .edn-unitChoice{padding:0 3px;font-size:11px}
  .edn-table .edn-unitMetaBox{padding:4px 5px;border:0;background:none}
  .edn-receivedValues span,.edn-receivedValues small{display:block;line-height:1.4}
  .edn-receivedValues small{margin-top:4px;color:var(--pu-muted);font-weight:500;font-size:11px}
  .edn-table .edn-poStatus{display:flex;flex-direction:column;gap:6px;min-height:0;padding:0}
  .edn-table .edn-poText{position:static;display:flex;align-items:center;justify-content:center;min-height:var(--edn-control-height);white-space:normal;font-size:11px;line-height:1.3}
  .edn-table .edn-total > div,.edn-table .edn-receivedValues > span{display:flex;align-items:center;min-height:var(--edn-control-height)}
  .edn-table .edn-total > div{justify-content:flex-end}
  .edn-table .edn-receivedValues > span{justify-content:center}
  .edn-table .complimentary-price__options{position:static;margin-top:3px;margin-left:auto;max-width:100%;white-space:normal;text-align:right}
  .edn-table .edn-removeLine{height:var(--edn-control-height)}
  .purchasing-receiving-ui .edn-table .edn-poStatus .edn-btn{min-width:0;width:100%;padding:6px;font-size:11px}
  .edn-table .edn-total .edn-diff{margin-top:6px;padding:3px 5px;font-size:10px;letter-spacing:0;text-transform:none}
  .edn-removeLine{width:26px;height:30px;padding:0;border:0;border-radius:5px;background:transparent;color:var(--pu-danger);font-size:20px;line-height:1;cursor:pointer}
  .edn-removeLine:hover{background:var(--pu-danger-bg)}
  @media (max-width:999px){.edn-grid--top{grid-template-columns:minmax(0,1fr)}.edn-grid--top>.edn-summary{contain:none;align-self:start}.edn-grid--top .edn-stats{grid-template-columns:repeat(4,minmax(0,1fr))}}
  @media (max-width:640px){.edn-wrap{padding:8px;align-items:flex-start}.edn-modal{width:100%;max-height:calc(100vh - 16px);border-radius:20px}.edn-head{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;padding:14px 14px 10px}.edn-title{font-size:1rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.edn-sub{margin-top:4px;font-size:.74rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.edn-close{min-width:76px;height:36px}.edn-body{padding:12px 14px 14px}.edn-grid--four,.edn-grid--two,.edn-stats,.edn-mobileGrid,.edn-impactStats,.edn-impactGrid,.edn-impactDeltaGrid{grid-template-columns:minmax(0,1fr)}.edn-sectionHead,.edn-expenseHead,.edn-actions,.edn-footer,.edn-footerActions,.edn-expenseActions{flex-direction:column;align-items:stretch}.edn-actions .edn-btn,.edn-footerActions .edn-btn,.edn-expenseActions .edn-btn{width:100%}.edn-confirmWrap,.edn-pickerWrap,.edn-receiveWrap,.edn-impactWrap{padding:10px;align-items:flex-start}.edn-confirm,.edn-picker,.edn-receive,.edn-impact{width:100%;border-radius:20px}.edn-picker,.edn-receive,.edn-impact{max-height:calc(100vh - 20px)}.edn-pickerHead{padding:14px 14px 10px}.edn-pickerBody{padding:12px 14px 14px}.edn-receiveTable{min-width:720px}.edn-poStatus{min-height:auto;padding-top:18px}.edn-impactHead{padding:15px 14px 12px}.edn-impactBody{padding:12px 14px 14px}.edn-impactFlag{display:none}.edn-impactActions{display:grid;grid-template-columns:minmax(0,1fr)}.edn-impactActions .edn-btn{width:100%}.edn-impactChangeHead{display:block}.edn-impactMode{display:inline-block!important;margin-top:7px!important}}
`;

const statusLabels = { draft: "Draft", verified: "Verified", verified_with_differences: "Verified with Differences", invoiced: "Invoiced", cancelled: "Cancelled" };
const cancellationBlockingMessage = (failure) => failure?.code === "DELIVERY_NOTE_EXCESS_LATER_CONSUMPTION"
  ? "Later consumption exceeds the quantity being cancelled. Review and reverse those consumption records manually before cancelling this delivery note."
  : failure?.message || failure?.error || "Cancellation is blocked. Reload the delivery note to check its stock.";
const formatMoney = (value) => (Number.isFinite(Number(value)) ? `${Number(value).toFixed(2)} EUR` : "-");
const hasDisplayValue = (value) => value !== null && value !== undefined && value !== "";
const formatOptionalMoney = (value) => (hasDisplayValue(value) ? formatMoney(value) : "-");
const formatQty = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return formatUnitNumber(numeric, 8);
};
const formatOptionalQty = (value) => (hasDisplayValue(value) ? formatQty(value) : "-");
function deliveryNoteReceivedValues(line) {
  const numberOrNull = (value) => hasDisplayValue(value) && String(value).trim() !== "" && Number.isFinite(Number(value))
    ? Number(value) : null;
  const receipts = Array.isArray(line.po_receipts) ? line.po_receipts : [];
  const quantities = receipts.map((receipt) => numberOrNull(receipt.quantity_received)).filter((value) => value !== null);
  // The API aggregate preserves posted receipt snapshots; legacy notes may only expose one receipt.
  const quantity = numberOrNull(line.received_quantity) ?? (quantities.length
    ? Number(quantities.reduce((sum, value) => sum + value, 0).toFixed(8)) : numberOrNull(line.quantity_received));
  // Do not present the first receipt's price as the price of every receipt, or invent an average.
  const prices = [...new Set(receipts.length
    ? receipts.map((receipt) => numberOrNull(receipt.unit_price_received)) : [numberOrNull(line.unit_price_received)])];
  return { quantity, prices, hasValues: quantity !== null || prices.some((price) => price !== null) };
}
function ReceivedValues({ values, unit, t, showLabel = false }) {
  return <div className="edn-receivedValues">
    <span title={t("Qty Received")}>{showLabel ? `${t("Received")}: ` : ""}{formatOptionalQty(values.quantity)}{hasDisplayValue(values.quantity) ? ` ${unit}` : ""}</span>
    {values.prices.map((price, index) => <small key={index} title={t("Price Received")}>
      {formatOptionalMoney(price)}{hasDisplayValue(price) ? `/${unit}` : ""}
    </small>)}
  </div>;
}
const compactEditableDecimal = (value) => {
  if (!hasDisplayValue(value)) return "";
  const rawValue = String(value).trim();
  if (!/^-?\d+\.\d+$/.test(rawValue)) return rawValue;
  return rawValue.replace(/(\.\d*?[1-9])0+$|\.0+$/, "$1");
};
const displayUnitPrice = (value) => hasDisplayValue(value) && Number.isFinite(Number(value))
  ? Number(value).toFixed(2).replace(/\.00$/, "") : value ?? "";
// Match the stored price precision without exposing floating-point residue.
const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-GB");
};
const formatUserLabel = (user) => {
  if (!user) return "-";
  return user.name || user.email || (user.id ? `User #${user.id}` : "-");
};
const formatDocumentInitiator = (record) => {
  const label = formatUserLabel(record?.initiated_by_user || record?.initiatedByUser);
  if (label !== "-") return label;
  const userId = record?.initiated_by_user_id || record?.initiatedByUserId;
  return userId ? `User #${userId}` : "-";
};
const getStatusTone = (status) => {
  if (status === "draft") return "edn-pill edn-pill--draft";
  if (["verified", "invoiced"].includes(status)) return "edn-pill edn-pill--verified";
  if (status === "verified_with_differences") return "edn-pill edn-pill--differences";
  if (status === "cancelled") return "edn-pill edn-pill--cancelled";
  return "edn-pill";
};
const normalizeLine = (sourceLine) => {
  const line = normalizeComplimentaryPrice(sourceLine);
  const quantityInDn = line?.quantity_in_dn ?? "";
  const isFreeStock = Boolean(line?.is_free_stock);
  const unitPriceInDn = isFreeStock ? "0" : line?.unit_price_in_dn ?? "";
  const hasInitialSnapshot = Boolean(line?.id && line?.unit_factor);
  const persistedSnapshot = line?.__persisted_unit_level
    ? {
        __persisted_unit_level: line.__persisted_unit_level,
        __persisted_unit_factor: line.__persisted_unit_factor,
        __persisted_unit_id: line.__persisted_unit_id,
        __persisted_selected_unit_name: line.__persisted_selected_unit_name,
        __persisted_inventory_unit_id: line.__persisted_inventory_unit_id,
        __persisted_inventory_unit_name: line.__persisted_inventory_unit_name,
      }
    : hasInitialSnapshot
      ? {
          __persisted_unit_level: line.unit_level || "unit",
          __persisted_unit_factor: line.unit_factor,
          __persisted_unit_id: line.unit_id,
          __persisted_selected_unit_name: line.selected_unit_name || line.unit_name,
          __persisted_inventory_unit_id: line.inventory_unit_id || line.item_unit_id,
          __persisted_inventory_unit_name: line.inventory_unit_name || line.unit_name,
        }
      : {};
  return {
    ...line,
    ...persistedSnapshot,
    unit_level: line?.unit_level || "unit",
    quantity_in_dn: quantityInDn,
    unit_price_in_dn: unitPriceInDn,
    is_free_stock: isFreeStock,
    free_stock_reason: isFreeStock || isExplicitZeroPrice(unitPriceInDn) ? line?.free_stock_reason || "" : "",
    total_price_in_dn: (Number(quantityInDn) || 0) * (Number(unitPriceInDn) || 0),
  };
};
// Saved lines may only have a destination ID, without an editor source marker.
// A destination matching the saved header follows it, including in drafts.
// Keep distinct saved destinations and explicit per-line selections as overrides.
const restoreDestinationInheritance = (line, headerLocationId) => (
  !line.destination_location_source && line.destination_location_id
    ? { ...line, destination_location_source: Number(line.destination_location_id) === Number(headerLocationId) ? "header" : "manual" }
    : line
);
const normalizeLoadedLine = (line) => normalizeLine({
  ...line,
  quantity_in_dn: compactEditableDecimal(line?.quantity_in_dn),
  // Preserve legacy blank prices; new lines keep their explicit price status.
  unit_price_in_dn: !line?.price_status && !line?.is_free_stock && isExplicitZeroPrice(line?.unit_price_in_dn)
    ? "" : compactEditableDecimal(line?.unit_price_in_dn),
});
const calculateLineTotal = (line) => (Number(line?.quantity_in_dn) || 0) * (Number(line?.unit_price_in_dn) || 0);
const calculateTotal = (lines = [], expenses = []) =>
  lines.reduce((sum, line) => sum + calculateLineTotal(line) * (Number(line.correction_direction) === -1 ? -1 : 1), 0) +
  expenses.filter((expense) => !expense?.distribute_cost).reduce((sum, expense) => sum + (Number(expense?.amount) || 0), 0);
const relationFor = findItemSupplierRelation;
const suggestedPrice = (item, supplierId) => {
  const relation = relationFor(item, supplierId);
  const value = relation?.purchase_price ?? relation?.price ?? item?.unit_price ?? item?.purchase_price ?? item?.price;
  return value === undefined || value === null || !Number.isFinite(Number(value)) ? "" : String(value);
};
const assignmentQty = (line) =>
  (line?.po_receipts || []).reduce((sum, receipt) => sum + (Number(receipt?.quantity_received ?? receipt?.quantity) || 0), 0);
function receivingValuesToCopy(line, source) {
  const receipts = line.po_receipts || [];
  const received = receipts.length ? assignmentQty(line) : line.received_quantity ?? line.quantity_received;
  const quantity = Number(received);
  const hasReceipt = receipts.length > 0 || Boolean(line.receipt_line_id) || hasDisplayValue(line.received_quantity) || quantity > 0;
  if (!hasReceipt || !hasDisplayValue(received) || !Number.isFinite(quantity) || quantity < 0) return null;
  const values = deliveryNoteInventoryValues(line, source);
  const hasPrice = hasDisplayValue(line.unit_price_received) && Number.isFinite(Number(line.unit_price_received)) && Number(line.unit_price_received) >= 0;
  const quantityPending = values.quantity === null || Math.abs(values.quantity - quantity) >= 1e-8;
  const pricePending = hasPrice && (values.unitPrice === null || Math.abs(values.unitPrice - Number(line.unit_price_received)) >= 1e-8);
  if (!quantityPending && !pricePending) return null;
  return {
    unit_level: hasPrice ? "unit" : line.unit_level,
    quantity_in_dn: String(quantity * (hasPrice ? 1 : values.factor)),
    unit_price_in_dn: hasPrice ? String(Number(line.unit_price_received)) : line.unit_price_in_dn,
  };
}
function ReceivedPurchaseOrderCheck({ line, label }) {
  if (line.difference_flag || assignmentQty(line) <= 0 || !(line.po_receipts || []).some((receipt) => Number(receipt.po_line_id) > 0)) return null;
  return <svg className="edn-receivedCheck" viewBox="0 0 16 16" role="img" aria-label={label}>
    <title>{label}</title><path d="m2.5 8 3.5 3.5 7.5-8" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
const pendingQtyFromPo = (line) => Number(line?.pending_quantity ?? line?.quantity_pending ?? 0) || 0;
const poLineLabel = (line) => line?.order?.order_code || line?.order_code || `PO line ${line?.id || line?.po_line_id || ""}`;
const poDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString("en-GB");
};
const buildInitialReceiveQuantities = (line, candidates) => {
  const existing = new Map((line?.po_receipts || []).map((receipt) => [String(receipt.po_line_id), String(receipt.quantity_received ?? "")]));
  if (existing.size) return Object.fromEntries(existing);

  let remaining = deliveryNoteInventoryValues(line, line).quantity || 0;
  return candidates.reduce((acc, candidate) => {
    const pending = pendingQtyFromPo(candidate);
    const nextQty = Math.max(Math.min(remaining, pending), 0);
    if (nextQty > 0) {
      acc[String(candidate.id || candidate.po_line_id)] = String(nextQty);
      remaining -= nextQty;
    }
    return acc;
  }, {});
};
const candidatesForLine = (line, pendingByItem) => {
  const pending = pendingByItem.get(String(line?.item_id)) || [];
  const byId = new Map(pending.map((candidate) => [String(candidate.id || candidate.po_line_id), candidate]));

  (line?.po_receipts || []).forEach((receipt) => {
    const key = String(receipt.po_line_id);
    if (!byId.has(key)) {
      byId.set(key, {
        id: receipt.po_line_id,
        po_line_id: receipt.po_line_id,
        item_id: line.item_id,
        order_code: receipt.order_code,
        expected_delivery_date: receipt.expected_delivery_date,
        quantity: receipt.ordered_quantity,
        ordered_quantity: receipt.ordered_quantity,
        quantity_received: receipt.po_quantity_received,
        pending_quantity: (Number(receipt.po_quantity_pending) || 0) + (Number(receipt.quantity_received) || 0),
        unit_price: receipt.unit_price_in_order,
      });
    }
  });

  return [...byId.values()];
};

const inventoryUnitSummary = (line, unitSource, t, languageId, documentType) => {
  if (["rectification", "supplier_return"].includes(documentType)) return "";
  const quantities = receivedInventoryQuantities(line, unitSource);
  const inventoryValues = deliveryNoteInventoryValues({ ...line, quantity_in_dn: quantities.inventory }, unitSource, undefined, languageId);
  if (!inventoryValues.option) return t("Select a valid unit");

  const inventoryLabel = inventoryValues.option.inventoryLabel;
  if (inventoryValues.quantity === null) return t("Stored in {unit}", { unit: inventoryLabel });

  const quantitySummary = `${formatUnitNumber(inventoryValues.quantity)} ${inventoryLabel}`;
  if (inventoryValues.unitPrice === null) return `→ ${quantitySummary}`;
  return `→ ${quantitySummary} · ${displayUnitPrice(inventoryValues.unitPrice)} EUR/${inventoryLabel}`;
};

function UnitLevelControl({ idPrefix, options, value, disabled, onChange }) {
  const { t } = useReceivingPopupUi();
  if (!options.length) return <span className="edn-unitRelation">{t("Unit not configured")}</span>;

  return (
    <div className="edn-unitPicker" role="group" aria-label={t("Unit on note")}>
      {options.map((option) => {
        const selected = option.level === value;
        return (
          <button
            key={`${idPrefix}-${option.level}`}
            type="button"
            className={`edn-unitChoice ${selected ? "edn-unitChoice--active" : ""}`}
            title={option.label}
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange(option.level)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function useDeliveryNoteUi() {
  const ui = useReceivingPopupUi();
  return useMemo(() => ({ ...ui, t: (message, values) => deliveryNoteLifecycleText(ui.languageId, message, values, ui.t) }), [ui]);
}

function stockBalanceVerificationError(requestError, lines, locations, t, draftSaved) {
  const data = requestError.response?.data;
  if (data?.code !== "LOCATION_LEDGER_DISCONTINUITY") return "";
  const line = data.item_id && lines.find((entry) => String(entry.item_id) === String(data.item_id));
  const item = line?.item_name || line?.description || line?.item_code ||
    (data.item_id ? `#${data.item_id}` : t("an item on this delivery note"));
  let reason = t("The stock balance for {item} differs from its movement history.", { item });
  if (data.location_id !== undefined) {
    const location = data.location_id === null ? t("Legacy / Unallocated")
      : locations.find((entry) => String(entry.id) === String(data.location_id))?.name || `#${data.location_id}`;
    reason = t("The stock balance for {item} ({location}) differs from its movement history.", { item, location });
  }
  const action = t("An administrator can reconcile this balance in Admin → Inventory Reconciliation.");
  return [reason, action, draftSaved && t("Your draft is saved. Verify it again after reconciliation.")].filter(Boolean).join(" ");
}

function validateCancellationLocations({ lines, selectedLocations }) {
  const groups = new Map();
  let valid = true;
  lines.forEach((line) => {
    const quantity = Number(line.stock_quantity_to_remove);
    if (!(quantity > 0)) return;
    const location = (line.locations || []).find((candidate) => String(candidate.id) === String(selectedLocations[line.line_id]));
    if (!location || !Number.isFinite(Number(location.available_qty))) { valid = false; return; }
    const key = `${line.item_id}:${location.id}`;
    const group = groups.get(key) || { quantity: 0, available: Number(location.available_qty), lineIds: [] };
    group.quantity = Number((group.quantity + quantity).toFixed(8));
    group.available = Math.min(group.available, Number(location.available_qty));
    group.lineIds.push(line.line_id);
    groups.set(key, group);
  });
  const shortageLineIds = new Set([...groups.values()].filter((group) => group.quantity > group.available).flatMap((group) => group.lineIds));
  return { valid: valid && shortageLineIds.size === 0, shortageLineIds };
}

function ConfirmModal({ open, title, message, busy, onClose, onConfirm, cancellation, reason, onReason, hasUnsavedEdits, cancellationPreview, cancellationReady, cancellationStockShortage, onCancellationLocation, onCancellationSearch, onRetryCancellation }) {
  const { themeId, t } = useDeliveryNoteUi();
  if (!open) return null;
  return (
    <div className="edn-confirmWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId} onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="edn-confirm" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <div className="edn-confirmHead"><h6>{t(title)}</h6></div>
        <div className="edn-confirmBody">
          <p>{t(message)}</p>
          {cancellation && <>
            <p className="edn-note">{t("Cancelled delivery notes are excluded from price histories and statistics and kept for audit.")}</p>
            {hasUnsavedEdits && <p className="edn-note">{t("Unsaved edits will be discarded. Cancellation uses the last saved delivery note.")}</p>}
            {cancellationPreview.status === "loading" && <p className="edn-note" role="status">{t("Checking stock and consumption before cancellation...")}</p>}
            {cancellationPreview.error && <div className="edn-banner edn-banner--error" role="alert">
              {t(cancellationPreview.error)} <button type="button" className="edn-btn" onClick={onRetryCancellation}>{t("Retry")}</button>
            </div>}
            {cancellationPreview.blockingError && <div className="edn-banner edn-banner--error" role="alert">{t(cancellationPreview.blockingError)}</div>}
            {cancellationStockShortage && <div className="edn-banner edn-banner--error" role="alert">{t("The selected locations do not have enough stock for all affected lines. Choose other locations or review the stock.")}</div>}
            <div className="edn-cancelLines">
              {cancellationPreview.lines.map((line) => {
                const locations = line.locations || [];
                const selected = cancellationPreview.selectedLocations[line.line_id];
                const destination = locations.find((location) => String(location.id) === String(selected));
                const query = (cancellationPreview.queries[line.line_id] || "").toLowerCase();
                return <section className="edn-section" key={line.line_id} aria-label={line.item_name}>
                  <strong>{line.item_name}</strong>
                  <p>{t("Quantity to reverse: {quantity} {unit}", { quantity: formatQty(line.quantity_to_reverse), unit: line.unit_name || "" })}</p>
                  {Number(line.automatic_consumption_quantity) > 0 && <p>{t("Automatic direct consumption: {quantity} {unit}. It will be reversed automatically.", { quantity: formatQty(line.automatic_consumption_quantity), unit: line.unit_name || "" })}</p>}
                  {Number(line.later_consumption_quantity) > 0 && <p>{t("Later consumption: {quantity} {unit}. These consumption records will remain unchanged.", { quantity: formatQty(line.later_consumption_quantity), unit: line.unit_name || "" })}</p>}
                  {Number(line.stock_quantity_to_remove) > 0 ? <>
                    <p>{t("Stock to remove: {quantity} {unit}", { quantity: formatQty(line.stock_quantity_to_remove), unit: line.unit_name || "" })}</p>
                    {line.requires_location_selection ? <>
                      <p className="edn-note">{t("Choose where to remove this stock.")}</p>
                      {!locations.some((location) => Number(location.available_qty) >= Number(line.stock_quantity_to_remove)) && <p role="alert">{t("No permitted location has enough stock for this item. Review its stock before cancelling.")}</p>}
                      <input type="search" className="form-control mt-2" placeholder={t("Search locations...")} aria-label={t("Search locations for {item}", { item: line.item_name })}
                        value={cancellationPreview.queries[line.line_id] || ""} onChange={(event) => onCancellationSearch(line.line_id, event.target.value)} />
                      <div className="edn-cancelLocations">
                        {locations.filter((location) => !query || `${location.name} ${location.code || ""}`.toLowerCase().includes(query)).map((location) =>
                          <button type="button" className="edn-pickerItem" key={location.id} aria-pressed={String(selected) === String(location.id)}
                            disabled={busy || !(Number(location.available_qty) >= Number(line.stock_quantity_to_remove))}
                            onClick={() => onCancellationLocation(line.line_id, location.id)}>
                            <strong>{location.name}</strong><span>{t("Available: {quantity} {unit}", { quantity: formatQty(location.available_qty), unit: line.unit_name || "" })}</span>
                          </button>)}
                      </div>
                      {!selected && <p className="edn-itemMeta">{t("Select a location to continue.")}</p>}
                    </> : <p>{t("Stock will be removed from: {location}", { location: destination?.name || t("Location unavailable") })}</p>}
                  </> : <p>{t("No stock needs to be removed from a location.")}</p>}
                </section>;
              })}
            </div>
            <label className="edn-label mt-3" htmlFor="edn-cancellation-reason">{t("Cancellation reason (optional)")}</label>
            <textarea id="edn-cancellation-reason" className="form-control" rows={3} maxLength={1000} value={reason} onChange={(event) => onReason(event.target.value)} disabled={busy} />
          </>}
          <div className="edn-footerActions mt-3">
            <button type="button" className="edn-btn edn-btn--soft" onClick={onClose} disabled={busy}>{t(cancellation ? "Keep delivery note" : "Cancel")}</button>
            <button type="button" className={`edn-btn ${cancellation ? "edn-btn--danger" : "edn-btn--primary"}`} onClick={onConfirm} disabled={busy || (cancellation && !cancellationReady)}>{busy ? t("Working...") : t(cancellation ? "Confirm cancellation" : "Continue")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ItemPickerModal({ open, items, query, supplierId, supplierName, onQuery, onClose, onSelect }) {
  const { themeId, t, languageId } = useDeliveryNoteUi();
  if (!open) return null;

  return (
    <div className="edn-pickerWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId} onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="edn-picker" onMouseDown={(event) => event.stopPropagation()}>
        <div className="edn-pickerHead">
          <div>
            <h6>{t("Add Item")}</h6>
            <p> {t("Items linked to")} {supplierName || t("this supplier")} {t("appear first. Search the full catalogue by item code or name.")} </p>
          </div>
          <button type="button" className="edn-btn edn-btn--soft" onClick={onClose}> {t("Close")} </button>
        </div>
        <div className="edn-pickerBody">
          <input
            autoFocus
            type="search"
            className="form-control"
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            placeholder={t("Search by item code or name...")}
          />
          <div className="edn-pickerList">
            {items.length ? (
              items.map((item) => {
                const linkedSupplier = itemMatchesSupplier(item, supplierId);
                const price = suggestedPrice(item, supplierId);
                const unit = getUnitName(item?.unit, languageId) || getUnitName(item?.subunit, languageId) || "-";
                return (
                  <button
                    key={`edn-picker-item-${item.id}`}
                    type="button"
                    className="edn-pickerItem"
                    onClick={() => onSelect(item)}
                  >
                    <strong>{item.code ? `${item.code} - ${item.name}` : item.name}</strong>
                    <span>
                      {[
                        `${t("Unit")}: ${unit}`,
                        price !== "" ? t("Suggested price: {price}", { price: formatMoney(price) }) : t("No suggested price"),
                      ].join(" · ")}
                    </span>
                    {linkedSupplier ? <span className="edn-pickerTag">{t("Linked supplier item")}</span> : null}
                  </button>
                );
              })
            ) : (
              <div className="edn-empty">
                <strong>{t("No matching items")}</strong>
                <span>{t("Try a different code or item name.")}</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ReceivePoModal({ open, line, candidates, quantities, busy, onQuantity, onClose, onApply }) {
  const { themeId, t, languageId } = useReceivingPopupUi();
  if (!open) return null;
  const total = Object.values(quantities || {}).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const inventoryValues = deliveryNoteInventoryValues(line, line, undefined, languageId);
  const lineQuantity = inventoryValues.quantity || 0;
  const inventoryUnit = inventoryValues.option?.inventoryLabel || "inventory units";
  const title = line?.item_name || line?.description || "Selected delivery note line";

  return (
    <div className="edn-receiveWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId} onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="edn-receive" onMouseDown={(event) => event.stopPropagation()}>
        <div className="edn-receiveHead">
          <div>
            <h6>{t("Receive against pending POs")}</h6>
            <p>{t(title)} {t("· Available")} {formatQty(lineQuantity)} {inventoryUnit}</p>
          </div>
          <button type="button" className="edn-btn edn-btn--soft" onClick={onClose} disabled={busy}> {t("Close")} </button>
        </div>
        <div className="edn-receiveBody">
          {candidates.length ? (
            <div className="edn-receiveTableWrap">
              <table className="edn-receiveTable">
                <thead>
                  <tr>
                    <th>{t("PO")}</th>
                    <th style={{ textAlign: "center" }}>{t("Expected")}</th>
                    <th style={{ textAlign: "center" }}>{t("Ordered")}</th>
                    <th style={{ textAlign: "center" }}>{t("Pending")}</th>
                    <th style={{ textAlign: "center" }}>{t("Receive")}</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((candidate) => {
                    const poLineId = String(candidate.id || candidate.po_line_id);
                    return (
                      <tr key={`edn-receive-po-${poLineId}`}>
                        <td>
                          <strong>{poLineLabel(candidate)}</strong>
                          <div className="edn-receiveMeta"> {t("Unit price")} {formatMoney(candidate.unit_price)} {t("· Line #")}{candidate.id || candidate.po_line_id}
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }}>{poDate(candidate.order?.expected_delivery_date || candidate.expected_delivery_date)}</td>
                        <td style={{ textAlign: "center" }}>{formatQty(candidate.quantity || candidate.ordered_quantity)}</td>
                        <td style={{ textAlign: "center" }}>{formatQty(pendingQtyFromPo(candidate))}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.00000001"
                            className="form-control edn-receiveInput"
                            value={quantities?.[poLineId] || ""}
                            onChange={(event) => onQuantity(poLineId, event.target.value)}
                            placeholder="0.00"
                            disabled={busy}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="edn-empty">
              <strong>{t("No pending purchase orders")}</strong>
              <span>{t("This supplier and item do not currently have pending PO quantities.")}</span>
            </div>
          )}
          <div className="edn-receiveFooter">
            <div className="edn-receiveTotal"> {t("Assigned")} {formatQty(total)} / {formatQty(lineQuantity)} {inventoryUnit}
            </div>
            <div className="edn-footerActions">
              <button type="button" className="edn-btn edn-btn--soft" onClick={onClose} disabled={busy}> {t("Cancel")} </button>
              <button type="button" className="edn-btn edn-btn--primary" onClick={onApply} disabled={busy || !candidates.length}>
                {busy ? t("Applying...") : t("Apply")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EditDeliveryNotePopup({ isOpen, deliveryNoteId, onClose, initialVerificationError }) {
  const { t, themeId, languageId } = useDeliveryNoteUi();
  const saveInProgress = useRef(false);
  const documentRequestRef = useRef(0);
  const historicalSnapshotRef = useRef(null);
  const editBaselineRef = useRef(null);
  const documentScopeRef = useRef(null);
  documentScopeRef.current = isOpen ? deliveryNoteId : null;
  const isMobile = useIsMobile(1000);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [deliveryNote, setDeliveryNote] = useState(null);
  const activeBusinessUnitId = readStoredUser()?.active_business_unit_id;
  const complimentary = useComplimentaryConfirmation({ isActive: isOpen,
    scopeKey: JSON.stringify([deliveryNoteId, activeBusinessUnitId, deliveryNote?.supplier_id, deliveryNote?.delivery_note_date]) });
  const [lines, setLines] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [editingPriceIndex, setEditingPriceIndex] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, type: "" });
  const originalViewer = useOriginalDocument(isOpen, deliveryNoteId);
  const [pendingPoLines, setPendingPoLines] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [savedLocationNames, setSavedLocationNames] = useState({});
  const [savedMainLocationIds, setSavedMainLocationIds] = useState([]);
  const [items, setItems] = useState([]);
  const [itemPicker, setItemPicker] = useState({ open: false, query: "" });
  const [loadingPendingPo, setLoadingPendingPo] = useState(false);
  const [receiveModal, setReceiveModal] = useState({ open: false, lineIndex: null, quantities: {}, busy: false });
  const [verifiedEditMode, setVerifiedEditMode] = useState(false);
  const [deletedLineIds, setDeletedLineIds] = useState([]);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancellationPreview, setCancellationPreview] = useState({ status: "idle", lines: [], selectedLocations: {}, queries: {} });
  const [cancellationPreviewAttempt, setCancellationPreviewAttempt] = useState(0);
  const [documentReloadRequired, setDocumentReloadRequired] = useState(false);
  const [unitRefreshError, setUnitRefreshError] = useState("");
  const initialVerificationErrorRef = useRef(initialVerificationError);
  const quantityReview = usePurchaseQuantityReview({
    documentType: "delivery_note", documentId: deliveryNoteId,
    currency: deliveryNote?.currency || "EUR",
    date: deliveryNote?.delivery_note_date, lines, enabled: isOpen && !["rectification", "supplier_return"].includes(deliveryNote?.document_type),
  });
  useEffect(() => {
    if (!isOpen || loading || !deliveryNote || !initialVerificationErrorRef.current) return;
    const requestError = initialVerificationErrorRef.current;
    initialVerificationErrorRef.current = null;
    if (requestError.response?.data?.code === "DELIVERY_NOTE_MAIN_LOCATION_NOT_ALLOWED") {
      setRejectedMainLocationId(requestError.response.data.location_id || null);
      setLocationRequestMessage("");
      setError("");
      setLocationRequest((previous) => previous + 1);
    } else if (!quantityReview.handleError(requestError)) {
      setError(stockBalanceVerificationError(requestError, lines, inventoryLocations, t, deliveryNote.status === "draft") ||
        deliveryNoteCorrectionError(requestError, languageId) || deliveryNoteDuplicateError(requestError, languageId) ||
        requestError.response?.data?.error || t("Could not verify this delivery note."));
    }
  }, [deliveryNote, inventoryLocations, isOpen, languageId, lines, loading, quantityReview, t]);

  const isCorrection = deliveryNote?.document_type === "rectification";
  const isReturn = deliveryNote?.document_type === "supplier_return";
  const ct = (message, values) => deliveryNoteCorrectionText(languageId, message, values);
  const isDraft = deliveryNote?.status === "draft" && !isReturn;
  const canReceivePurchaseOrders = !isCorrection && isDraft && (deliveryNote.is_direct_delivery_note ?? !deliveryNote.receiving_id);
  const hasLinkedCorrections = !isCorrection && lines.some((line) => Number(line.correction_resolved_quantity) > 0);
  const verificationStatus = deliveryNote?.status === "invoiced" ? deliveryNote.persisted_status : deliveryNote?.status;
  const canReopen = !isReturn && !isCorrection && ["verified", "verified_with_differences"].includes(verificationStatus);
  const currentUser = readStoredUser();
  const canCancel = ((verifiedEditMode && !unitRefreshError && canReopen && !hasLinkedCorrections) || (isCorrection && deliveryNote?.status === "verified")) && (Number(currentUser?.id) === 1 || currentUser?.permissions?.access_settings || currentUser?.permissions?.["purchasing.delivery_notes.update"]);
  const cancellationLocationValidation = validateCancellationLocations(cancellationPreview);
  const cancellationReady = cancellationPreview.status === "ready" && !cancellationPreview.blockingError && cancellationLocationValidation.valid;
  useEffect(() => {
    if (!isOpen || !canCancel || !confirmState.open || confirmState.type !== "cancel") return;
    let current = true;
    setCancellationPreview({ status: "loading", lines: [], selectedLocations: {}, queries: {} });
    axios.get(`/api/purchasing/receivings/delivery-notes/${deliveryNoteId}/cancellation-preview`).then((response) => {
      if (!current) return;
      if (!Array.isArray(response.data?.lines)) throw new Error("Invalid preview");
      const selectedLocations = {};
      response.data.lines.forEach((line) => {
        if (Number(line.stock_quantity_to_remove) > 0 && !line.requires_location_selection && line.default_location_id) selectedLocations[line.line_id] = line.default_location_id;
      });
      const { shortageLineIds } = validateCancellationLocations({ lines: response.data.lines, selectedLocations });
      const previewLines = response.data.lines.map((line) => shortageLineIds.has(line.line_id) ? { ...line, requires_location_selection: true } : line);
      setCancellationPreview({ status: "ready", lines: previewLines, selectedLocations, queries: {}, blockingError: response.data.blocking_error ? cancellationBlockingMessage(response.data.blocking_error) : "" });
    }).catch((requestError) => {
      if (current) setCancellationPreview({ status: "error", lines: [], selectedLocations: {}, queries: {}, error: requestError.response?.data ? cancellationBlockingMessage(requestError.response.data) : "Could not check cancellation details. Retry before cancelling." });
    });
    return () => { current = false; };
  }, [canCancel, cancellationPreviewAttempt, confirmState.open, confirmState.type, deliveryNoteId, isOpen]);
  const editingNote = !documentReloadRequired && (isDraft || (canReopen && verifiedEditMode));
  const canEditSupplierValues = editingNote && !unitRefreshError;
  const canEditNumber = editingNote && deliveryNote.status !== "invoiced";
  const hasEditedNumber = (deliveryNote?.delivery_note_number ?? "") !== (historicalSnapshotRef.current?.note?.delivery_note_number ?? "");
  const inventoryPreviewLine = (line) => canEditSupplierValues && line.inventory_quantity_source === "document"
    ? { ...line, inventory_quantity_snapshot: null } : line;
  const changeLineDestination = useCallback((index, destination, source) => {
    setLines((previous) => previous.map((line, i) => i === index ? { ...line, destination_location_id: destination, destination_location_source: source } : line));
  }, []);
  const { suggestions: destinationSuggestions, error: destinationError } = useReceivingDestinations({
    lines, headerLocationId: deliveryNote?.destination_location_id, scopeKey: deliveryNote?.id,
    disabled: !isOpen || !canEditSupplierValues || isCorrection, onChange: changeLineDestination,
    requireAssignedDestination: true,
  });
  const [locationRequest, setLocationRequest] = useState(0);
  const [locationRequestMessage, setLocationRequestMessage] = useState("");
  const [rejectedMainLocationId, setRejectedMainLocationId] = useState(null);
  const pendingVerificationLocationRef = useRef(null);
  const mainIsUnavailable = (id) => Boolean(id) && (String(id) === String(rejectedMainLocationId)
    || isUnavailableMainLocation(id, inventoryLocations, savedMainLocationIds));
  const generalDestinationLocations = (Object.values(destinationSuggestions)[0]?.locations || inventoryLocations)
    .filter((location) => !mainIsUnavailable(location.id));
  const locationName = (id) => savedLocationNames[id] || inventoryLocations.find((location) => String(location.id) === String(id))?.name;
  const editedLineDestination = (line, headerLocationId = deliveryNote?.destination_location_id) =>
    deliveryNoteLineLocation(line, headerLocationId, destinationSuggestions[line.item_id], {
      posted: !isDraft && Boolean(line.id) && !line.destination_location_id && !headerLocationId,
    });
  const lineLocationControl = (line, index) => {
    if (isCorrection) return null;
    const destination = deliveryNoteLineLocation(line, deliveryNote?.destination_location_id, destinationSuggestions[line.item_id], {
      posted: !canEditSupplierValues || (!isDraft && !line.destination_location_id && !deliveryNote?.destination_location_id),
    });
    return <DeliveryNoteLocationPicker compact
      value={destination || ""}
      automatic={!line.destination_location_id && !deliveryNote?.destination_location_id}
      selectionRequired={canEditSupplierValues && mainIsUnavailable(destination)}
      resolvedName={locationName(destination)}
      generalLocationId={deliveryNote?.destination_location_id}
      locations={canEditSupplierValues ? (destinationSuggestions[line.item_id]?.locations || inventoryLocations).filter((location) => !mainIsUnavailable(location.id)) : inventoryLocations}
      label={`${t("Destination Location")}: ${line.item_name || line.description || t("Unnamed item")}`}
      disabled={!canEditSupplierValues || Boolean(busyAction) || receiveModal.busy}
      onChange={(destination, source) => changeLineDestination(index, destination, source)} />;
  };
  const requestLocationSelection = (message, resumeVerification = false) => {
    pendingVerificationLocationRef.current = resumeVerification ? { deliveryNoteId, businessUnitId: activeBusinessUnitId } : null;
    setLocationRequestMessage(message);
    setError(message ? t(message) : "");
    setLocationRequest((previous) => previous + 1);
  };
  const handleMainLocationError = (requestError) => {
    const data = requestError.response?.data;
    if (data?.code !== "DELIVERY_NOTE_MAIN_LOCATION_NOT_ALLOWED") return false;
    setRejectedMainLocationId(data.location_id || null);
    requestLocationSelection("");
    return true;
  };
  const requireLineDestinations = (sourceLines = lines, headerLocationId = deliveryNote?.destination_location_id, { saving = false } = {}) => {
    if (isCorrection) return true;
    const destinations = sourceLines.map((line) => editedLineDestination(line, headerLocationId));
    const blockedMain = mainIsUnavailable(headerLocationId) || destinations.some(mainIsUnavailable);
    if (!blockedMain && (saving || destinations.every(Boolean))) return true;
    requestLocationSelection(blockedMain ? ""
      : "Select a destination for each unresolved item before verifying the delivery note.", !saving);
    return false;
  };

  const total = useMemo(() => isReturn ? -Math.abs(calculateTotal(lines, expenses)) : calculateTotal(lines, expenses), [expenses, isReturn, lines]);
  const hasPendingPrices = lines.some((line) => line.price_status === "missing" || line.price_status === "estimated");
  const differencesCount = useMemo(() => lines.filter((line) => Boolean(line?.difference_flag)).length, [lines]);
  const receivedValues = useMemo(() => lines.map(deliveryNoteReceivedValues), [lines]);
  const showReceivedColumn = receivedValues.some((values) => values.hasValues);
  const pendingByItem = useMemo(() => {
    const grouped = new Map();
    pendingPoLines.forEach((line) => {
      const pending = pendingQtyFromPo(line);
      if (!Number.isFinite(pending) || pending <= 0) return;
      const key = String(line.item_id);
      grouped.set(key, [...(grouped.get(key) || []), line]);
    });
    return grouped;
  }, [pendingPoLines]);
  const itemMap = useMemo(() => new Map(items.map((item) => [String(item.id), item])), [items]);
  const hasPendingReceivingValues = useMemo(() => lines.some((line) =>
    receivingValuesToCopy(line, mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line))), [lines, itemMap]);
  const filteredItems = useMemo(() => {
    const query = itemPicker.query.trim().toLowerCase();
    return [...items]
      .filter((item) => item.is_active !== false)
      .filter((item) =>
        !query ||
        [item?.code, item?.name]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      )
      .sort((left, right) => {
        const supplierDifference =
          Number(itemMatchesSupplier(right, deliveryNote?.supplier_id)) -
          Number(itemMatchesSupplier(left, deliveryNote?.supplier_id));
        if (supplierDifference) return supplierDifference;
        return String(left?.name || "").localeCompare(String(right?.name || ""), "en", { sensitivity: "base" });
      });
  }, [deliveryNote?.supplier_id, itemPicker.query, items]);

  const fetchDeliveryNote = useCallback(async ({ quiet = false } = {}) => {
    const request = ++documentRequestRef.current;
    const isCurrent = () => request === documentRequestRef.current && documentScopeRef.current === deliveryNoteId;
    try {
      if (!quiet) setLoading(true);
      if (!quiet) setError("");
      const [response, locationsResponse, itemsResponse] = await Promise.all([
        axios.get(`/api/purchasing/receivings/delivery-notes/${deliveryNoteId}`),
        axios.get("/api/purchasing/inventory/locations?include_inactive=false"),
        axios.get("/api/purchasing/items", { params: { status: "all" } }).catch((itemsError) => {
          console.error("[EditDeliveryNotePopup] Error loading item catalogue:", itemsError);
          return { data: [] };
        }),
      ]);
      if (!isCurrent()) return;
      const note = response.data?.delivery_note ? {
        ...response.data.delivery_note,
        is_direct_delivery_note: response.data.is_direct_delivery_note,
        original_delivery_note_number: response.data.original_delivery_note?.delivery_note_number,
      } : null;
      const catalogue = Array.isArray(itemsResponse.data) ? itemsResponse.data : [];
      let loadedLines = Array.isArray(response.data?.lines)
        ? response.data.lines.map((line) => normalizeLoadedLine(restoreDestinationInheritance(line, note?.destination_location_id))) : [];
      if (note?.document_type === "rectification") loadedLines = loadedLines.map((line) => correctionUnitSource(line, catalogue.find((item) => String(item.id) === String(line.item_id))));
      historicalSnapshotRef.current = JSON.parse(JSON.stringify({ note, lines: loadedLines, expenses: response.data?.expenses || [], catalogue }));
      let refreshError = "";
      if (note?.status === "draft" && !["supplier_return", "rectification"].includes(note?.document_type)) {
        try { loadedLines = loadedLines.map((line) => refreshDeliveryNoteLineUnits(line, catalogue.find((item) => String(item.id) === String(line.item_id)))); }
        catch (refreshFailure) { refreshError = refreshFailure.message; }
      }
      setUnitRefreshError(refreshError);
      setDeliveryNote(note);
      // Display saved destinations independently of the locations the user may
      // select. Index names by ID so editing a destination cannot reuse its old label.
      const locationNames = {};
      const mainLocationIds = new Set();
      const rememberLocation = (source, prefix = "destination_location") => {
        if (source?.[`${prefix}_id`] && source?.[`${prefix}_name`]) locationNames[source[`${prefix}_id`]] = source[`${prefix}_name`];
        if (source?.[`${prefix}_id`] && source?.[`${prefix}_is_main`] === true) mainLocationIds.add(source[`${prefix}_id`]);
      };
      rememberLocation(note);
      rememberLocation(note?.receiving);
      loadedLines.forEach((line) => {
        rememberLocation(line);
        rememberLocation(line, "posted_destination_location");
        (line.po_receipts || []).forEach((receipt) => {
          rememberLocation(receipt);
          rememberLocation(receipt, "po_line_destination_location");
          rememberLocation(receipt, "po_destination_location");
        });
      });
      setSavedLocationNames(locationNames);
      setSavedMainLocationIds([...mainLocationIds]);
      setInventoryLocations(Array.isArray(locationsResponse.data) ? locationsResponse.data : []);
      setItems(catalogue);
      const loadedExpenses = Array.isArray(response.data?.expenses) ? response.data.expenses : [];
      editBaselineRef.current = { note, lines: loadedLines, expenses: loadedExpenses };
      setLines(loadedLines);
      setDeletedLineIds([]);
      setDocumentReloadRequired(false);
      setEditingPriceIndex(null);
      setExpenses(loadedExpenses);
      return true;
    } catch (requestError) {
      if (!isCurrent()) return;
      console.error("[EditDeliveryNotePopup] Error loading delivery note:", requestError);
      if (requestError.response?.status === 404) {
        setDeliveryNote(null);
        setLines([]);
        setExpenses([]);
        setError("Delivery note not found.");
      } else {
        setError("Could not load this delivery note.");
      }
      return false;
    } finally {
      if (!quiet && isCurrent()) setLoading(false);
    }
  }, [deliveryNoteId]);

  useEffect(() => {
    if (!isOpen) {
      documentRequestRef.current += 1;
      historicalSnapshotRef.current = null;
      setUnitRefreshError("");
      setLoading(false);
      setBusyAction("");
      setDeliveryNote(null);
      setLines([]);
      setExpenses([]);
      setError("");
      setNotice(null);
      setConfirmState({ open: false, type: "" });
      setPendingPoLines([]);
      setInventoryLocations([]);
      setSavedLocationNames({});
      setItems([]);
      setItemPicker({ open: false, query: "" });
      setLoadingPendingPo(false);
      setReceiveModal({ open: false, lineIndex: null, quantities: {}, busy: false });
      setVerifiedEditMode(false);
      return;
    }
    historicalSnapshotRef.current = null;
    setDeliveryNote(null); setLines([]); setUnitRefreshError(""); setBusyAction("");
    setDeletedLineIds([]); setCancellationReason(""); setDocumentReloadRequired(false); setConfirmState({ open: false, type: "" });
    if (deliveryNoteId) fetchDeliveryNote();
    return () => { documentRequestRef.current += 1; };
  }, [deliveryNoteId, fetchDeliveryNote, isOpen]);

  useEffect(() => {
    setLocationRequest(0);
    setLocationRequestMessage("");
    setRejectedMainLocationId(null);
    pendingVerificationLocationRef.current = null;
  }, [deliveryNoteId, isOpen, activeBusinessUnitId]);

  useEffect(() => {
    setVerifiedEditMode(false);
  }, [deliveryNoteId]);

  useEffect(() => {
    if (!deliveryNote) return;
    const nextTotal = total.toFixed(2);
    if (String(deliveryNote.total_amount ?? "") !== nextTotal) {
      setDeliveryNote((previous) => (previous ? { ...previous, total_amount: nextTotal } : previous));
    }
  }, [deliveryNote, total]);

  useEffect(() => {
    if (!isOpen || !deliveryNote?.supplier_id || !canReceivePurchaseOrders) {
      setPendingPoLines([]);
      setLoadingPendingPo(false);
      return;
    }

    const itemIds = [...new Set(lines.map((line) => line.item_id).filter(Boolean).map(String))];
    if (!itemIds.length) {
      setPendingPoLines([]);
      return;
    }

    let cancelled = false;
    const loadPendingPoLines = async () => {
      setLoadingPendingPo(true);
      try {
        const response = await axios.get("/api/purchasing/delivery-notes-direct/pending-po-lines", {
          params: { supplier_id: deliveryNote.supplier_id, item_ids: itemIds.join(",") },
        });
        if (!cancelled) setPendingPoLines(Array.isArray(response.data) ? response.data : []);
      } catch (requestError) {
        console.error("[EditDeliveryNotePopup] Error loading pending PO lines:", requestError);
        if (!cancelled) setPendingPoLines([]);
      } finally {
        if (!cancelled) setLoadingPendingPo(false);
      }
    };
    loadPendingPoLines();
    return () => {
      cancelled = true;
    };
  }, [canReceivePurchaseOrders, deliveryNote?.supplier_id, isOpen, lines]);

  const loadCurrentUnitLines = async (sourceLines = lines) => {
    const response = await axios.get("/api/purchasing/items", { params: { status: "all" } }).catch(() => {
      throw new Error("Current item units could not be loaded. Reload the delivery note before editing.");
    });
    if (!Array.isArray(response.data)) throw new Error("Current item units could not be loaded. Reload the delivery note before editing.");
    const catalogue = response.data;
    const refreshed = sourceLines.map((line) => refreshDeliveryNoteLineUnits(line,
      catalogue.find((item) => String(item.id) === String(line.item_id)),
      { preserveSnapshot: !isDraft && line.inventory_quantity_snapshot != null }));
    return { catalogue, refreshed };
  };

  const startEditing = async () => {
    if (busyAction || saveInProgress.current || documentReloadRequired) return;
    const request = ++documentRequestRef.current;
    const isCurrent = () => request === documentRequestRef.current && documentScopeRef.current === deliveryNoteId;
    setBusyAction("refresh_units"); setError("");
    try {
      const { catalogue, refreshed } = await loadCurrentUnitLines();
      if (!isCurrent()) return;
      const editableLines = refreshed.map((line) => restoreDestinationInheritance(line, deliveryNote?.destination_location_id));
      if (!isDraft && !verifiedEditMode) editBaselineRef.current = { note: deliveryNote, lines: editableLines, expenses };
      else if (editBaselineRef.current?.lines === lines) editBaselineRef.current.lines = editableLines;
      setItems(catalogue); setLines(editableLines); setUnitRefreshError("");
      if (!isDraft) setVerifiedEditMode(true);
    } catch (refreshFailure) {
      if (isCurrent()) {
        setUnitRefreshError(refreshFailure.response ? "Current item units could not be loaded. Reload the delivery note before editing." : refreshFailure.message);
        if (!isDraft) setVerifiedEditMode(true);
      }
    } finally {
      if (isCurrent()) setBusyAction("");
    }
  };

  const setLineField = (index, field, value) => {
    if (["unit_price_in_dn", "item_id", "quantity_in_dn", "unit_level"].includes(field)) complimentary.forgetLine(lines[index], index);
    setLines((previousLines) => previousLines.map((line, lineIndex) => (
      lineIndex === index
        ? normalizeLine(updateComplimentaryLine(line, field, value, {
            priceKey: "unit_price_in_dn",
            identityFields: ["item_id", "quantity_in_dn", "unit_level"],
          }))
        : line
    )));
    setError("");
  };

  const getReferencePrice = (line) => fetchDeliveryNoteReferencePrice({ line,
    item: mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line), businessUnitId: activeBusinessUnitId,
    supplierId: deliveryNote?.supplier_id, date: deliveryNote?.delivery_note_date,
    currency: deliveryNote?.currency || "EUR", excludeDeliveryNoteId: deliveryNoteId, languageId });

  const editLinePrice = async (index) => {
    const source = lines[index];
    const prepared = await complimentary.editLine(source, {
      getLastPrice: getReferencePrice,
      getItemLabel: (line) => line.item_name || itemMap.get(String(line.item_id))?.name || line.description || t("Item"),
      getUnitLabel: (line) => buildDeliveryNoteUnitOptions(mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line), undefined, languageId)
        .find((option) => option.level === line.unit_level)?.label || line.unit_name || line.unit_level,
    });
    if (!prepared) return;
    setLines((previous) => previous.map((line) => line === source ? normalizeLine(prepared) : line));
    setError("");
  };

  const addItem = () => {
    if (!canEditSupplierValues || busyAction) return;
    setItemPicker({ open: true, query: "" });
    setError("");
  };

  const selectItem = (item) => {
    if (!item?.id || !canEditSupplierValues || busyAction) return;
    const price = suggestedPrice(item, deliveryNote?.supplier_id);
    const newLine = normalizeLine({
      temp_id: `draft-line-${Date.now()}-${item.id}`,
      item_id: item.id,
      item_code: item.code || "",
      item_name: item.name || "",
      description: item.name || null,
      unit_name: item?.unit?.name || "",
      subunit_name: item?.subunit?.name || "",
      subunit2_name: item?.subunit2?.name || "",
      pack_size: item.pack_size || null,
      subunit2_size: item.subunit2_size || null,
      unit_level: "unit",
      quantity_received: null,
      quantity_in_dn: "",
      unit_price_in_dn: price,
      destination_location_id: deliveryNote?.destination_location_id || null,
      difference_flag: false,
      is_free_stock: false,
      free_stock_reason: "",
      po_receipts: [],
    });
    setLines((previousLines) => [newLine, ...previousLines]);
    setItemPicker({ open: false, query: "" });
    setNotice({ tone: "success", text: t(isDraft ? "{name} added to the draft. Save changes to persist it." : "{name} added. Save changes to update the delivery note and its stock.", { name: item.name || t("Item") }) });
    setError("");
  };

  const removeLine = (index) => {
    if (!canEditSupplierValues || busyAction) return;
    const line = lines[index];
    if (!isDraft && line?.id) setDeletedLineIds((previous) => [...new Set([...previous, line.id])]);
    setLines((previousLines) => previousLines.filter((_, lineIndex) => lineIndex !== index));
    setReceiveModal((previous) =>
      previous.lineIndex === index
        ? { open: false, lineIndex: null, quantities: {}, busy: false }
        : previous
    );
    setNotice({
      tone: assignmentQty(line) > 0 ? "warning" : "success",
      text: !isDraft ? "Line removed locally. Save changes to reverse its posted stock and update the delivery note." : assignmentQty(line) > 0
        ? "Line removed from the draft. Its pending PO receiving link will also be removed when you save."
        : "Line removed from the draft. Save changes to persist the removal.",
    });
    setError("");
  };

  const copyFromReceiving = () => {
    if (!hasPendingReceivingValues || unitRefreshError) return;

    setLines((previousLines) =>
      previousLines.map((line) => {
        const unitSource = mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line);
        const copiedValues = receivingValuesToCopy(line, unitSource);
        if (!copiedValues) return line;
        return normalizeLine(Object.entries(copiedValues).reduce((nextLine, [field, value]) =>
          updateComplimentaryLine(nextLine, field, value, {
            priceKey: "unit_price_in_dn", identityFields: ["quantity_in_dn", "unit_level"],
          }), line));
      })
    );
    setNotice({
      tone: "success",
      text: "Quantities and prices were copied from the linked receiving data.",
    });
  };

  const openReceiveModal = (index) => {
    if (!canReceivePurchaseOrders || loadingPendingPo) return;
    const line = lines[index];
    if (!line?.item_id) {
      setError(t("This delivery note line is not linked to an item."));
      return;
    }
    if (!line.id || !(pendingByItem.get(String(line.item_id)) || []).length) return;
    const candidates = candidatesForLine(line, pendingByItem);
    setReceiveModal({
      open: true,
      lineIndex: index,
      quantities: buildInitialReceiveQuantities(line, candidates),
      busy: false,
    });
    setError("");
  };

  const setReceiveQuantity = (poLineId, value) => {
    setReceiveModal((previous) => ({
      ...previous,
      quantities: { ...(previous.quantities || {}), [String(poLineId)]: value },
    }));
  };

  const applyReceiveAssignments = async () => {
    if (!canReceivePurchaseOrders || saveInProgress.current || busyAction || unitRefreshError) return;
    const index = receiveModal.lineIndex;
    const line = lines[index];
    if (index === null || index === undefined || !line?.id) return;

    const candidates = candidatesForLine(line, pendingByItem);
    const candidateMap = new Map(candidates.map((candidate) => [String(candidate.id || candidate.po_line_id), candidate]));
    const assignments = Object.entries(receiveModal.quantities || {})
      .map(([poLineId, value]) => ({
        delivery_note_line_id: line.id,
        po_line_id: Number(poLineId),
        quantity_received: Number(value),
      }))
      .filter((assignment) => Number.isFinite(assignment.quantity_received) && assignment.quantity_received > 0);

    const unitSource = mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line);
    const lineQuantity = deliveryNoteInventoryValues(line, unitSource).quantity || 0;
    const totalAssigned = assignments.reduce((sum, assignment) => sum + assignment.quantity_received, 0);
    if (lineQuantity > 0 && totalAssigned > lineQuantity + 0.0001) {
      setError(t("The quantity assigned to purchase orders cannot exceed the delivery note quantity converted to the item's main unit."));
      return;
    }

    for (const assignment of assignments) {
      const candidate = candidateMap.get(String(assignment.po_line_id));
      const existingQty = (line.po_receipts || [])
        .filter((receipt) => String(receipt.po_line_id) === String(assignment.po_line_id))
        .reduce((sum, receipt) => sum + (Number(receipt.quantity_received) || 0), 0);
      const pending = pendingQtyFromPo(candidate) + existingQty;
      if (!candidate || assignment.quantity_received > pending + 0.0001) {
        setError(t("One of the assigned quantities is higher than the pending PO quantity."));
        return;
      }
    }

    saveInProgress.current = true;
    setReceiveModal((previous) => ({ ...previous, busy: true }));
    setError("");
    try {
      const preparedLines = isDraft ? lines.map((line) => normalizeComplimentaryPrice(line)) : await confirmComplimentaryLines();
      if (!preparedLines) return;
      setLines(preparedLines);
      await patchDeliveryNoteLines(buildPayload(preparedLines));
      await axios.post(`/api/purchasing/delivery-notes-direct/${deliveryNoteId}/po-receipts`, {
        assignments: assignments.length ? assignments : [{ delivery_note_line_id: line.id }],
      });
      setReceiveModal({ open: false, lineIndex: null, quantities: {}, busy: false });
      setNotice({ tone: "success", text: "Pending purchase order quantities were linked to this delivery note line." });
      await fetchDeliveryNote({ quiet: true });
    } catch (requestError) {
      console.error("[EditDeliveryNotePopup] Error applying PO receiving links:", requestError);
      setError(deliveryNoteNumberError(requestError, languageId) || requestError.response?.data?.error || requestError.message || t("Could not link this line to the pending purchase orders."));
    } finally {
      saveInProgress.current = false;
      setReceiveModal((previous) => ({ ...previous, busy: false }));
    }
  };

  const confirmComplimentaryLines = (options = {}, sourceLines = lines) => complimentary.confirmLines(sourceLines, {
    getLastPrice: getReferencePrice,
    priceKey: "unit_price_in_dn",
    quantityKey: "quantity_in_dn",
    getItemLabel: (line) => line.item_name || itemMap.get(String(line.item_id))?.name || line.description || t("Item"),
    getUnitLabel: (line) => buildDeliveryNoteUnitOptions(mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line), undefined, languageId)
      .find((option) => option.level === line.unit_level)?.label || line.unit_name || line.unit_level,
    ...options,
  });

  const numberChange = () => {
    const original = historicalSnapshotRef.current?.note?.delivery_note_number ?? null;
    const input = deliveryNote?.delivery_note_number ?? "";
    if (input === (original ?? "")) return null;
    const number = input.trim();
    if (!number || number.length > 100) throw new Error(t("Enter a delivery note number of up to 100 characters."));
    return { delivery_note_number: number, expected_delivery_note_number: original };
  };

  const buildPayload = (sourceLines = lines, headerLocationId = deliveryNote?.destination_location_id) => {
    if (unitRefreshError) throw new Error(unitRefreshError);
    if (isCorrection) {
      const correctionError = validateCorrectionLines(sourceLines, languageId);
      if (correctionError) throw new Error(correctionError);
    }
    const payloadLines = sourceLines.map((line) => {
      const option = buildDeliveryNoteUnitOptions(mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line))
        .find((entry) => entry.level === line.unit_level);
      if (!option) throw new Error("Current item units could not be loaded. Reload the delivery note before editing.");
      return { ...line, expiration_date: expirationDatePayload(line, itemMap.get(String(line.item_id))), total_price_in_dn: calculateLineTotal(line), expected_unit_factor: option.factor,
        expected_unit_id: option.unitId, expected_inventory_unit_id: option.inventoryUnitId };
    });
    const payloadExpenses = prepareDeliveryNoteAdjustments(expenses, payloadLines);
    return {
      ...numberChange(),
      document_type: deliveryNote?.document_type || "standard",
      correction_manual_pending: Boolean(deliveryNote?.correction_manual_pending),
      original_delivery_note_id: deliveryNote?.original_delivery_note_id || null,
      delivery_note_date: deliveryNote?.delivery_note_date || null,
      expected_delivery_note_date: historicalSnapshotRef.current?.note?.delivery_note_date || null,
      destination_location_id: headerLocationId || null,
      lines: payloadLines,
      ...(!isDraft ? { deleted_line_ids: deletedLineIds } : {}),
      expenses: payloadExpenses,
      total_amount: calculateTotal(payloadLines, payloadExpenses).toFixed(2),
    };
  };

  const patchDeliveryNoteLines = async (payload) => {
    const response = await axios.patch(`/api/purchasing/receivings/delivery-notes/${deliveryNoteId}/lines`, payload);
    const snapshot = historicalSnapshotRef.current;
    if (documentScopeRef.current === deliveryNoteId && String(snapshot?.note?.id) === String(deliveryNoteId)) {
      const header = { ...snapshot.note };
      if ("delivery_note_date" in payload) header.delivery_note_date = payload.delivery_note_date;
      if ("delivery_note_number" in payload) {
        header.delivery_note_number = response.data?.delivery_note_number ?? payload.delivery_note_number;
        setDeliveryNote((previous) => ({ ...previous, delivery_note_number: header.delivery_note_number }));
        window.dispatchEvent(new CustomEvent("refreshDeliveryNotesList"));
      }
      historicalSnapshotRef.current = { ...snapshot, note: header };
    }
    return response;
  };

  const saveChanges = async () => {
    if (saveInProgress.current || busyAction || (!canEditSupplierValues && !(canEditNumber && hasEditedNumber))) return;
    saveInProgress.current = true;
    setBusyAction("save");
    setError("");
    try {
      const number = numberChange();
      const baseline = editBaselineRef.current;
      // Unit refreshes and the derived total are display changes. A reference correction
      // should not submit quantities, prices or stock movements from historical lines.
      if (number && baseline?.lines === lines && baseline.expenses === expenses && !deletedLineIds.length &&
        Object.keys(deliveryNote).every((key) => key === "delivery_note_number" || key === "total_amount" || deliveryNote[key] === baseline.note[key])) {
        await patchDeliveryNoteLines(number);
        if (documentScopeRef.current !== deliveryNoteId) return;
        setVerifiedEditMode(false);
        if (!isDraft) setLines(historicalSnapshotRef.current.lines);
        setNotice({ tone: "success", text: t("Delivery note number updated.") });
        await fetchDeliveryNote({ quiet: true });
        return;
      }
      if (unitRefreshError) { setError(t(unitRefreshError)); return; }
      if (!isDraft && !lines.length && deletedLineIds.length) {
        setError(t("Keep at least one item, or cancel the delivery note to reverse it completely."));
        return;
      }
      if (!requireLineDestinations(lines, deliveryNote?.destination_location_id, { saving: true })) return;
      const preparedLines = await confirmComplimentaryLines({ includeBlank: !isDraft });
      if (!preparedLines) return;
      if (!isDraft && preparedLines.some((line) => !(Number(line.quantity_in_dn) > 0) ||
        !isDeliveryNotePriceValidForVerification(line))) {
        setError(t("Every line needs a positive quantity and a valid price, or an explicit Complimentary or Missing choice."));
        return;
      }
      const payload = buildPayload(preparedLines);
      setLines(preparedLines);
      if (!isDraft) {
        const reviewResult = isCorrection ? { allowed: true } : await quantityReview.check({ lines: preparedLines });
        if (!reviewResult.allowed) return;
        if (reviewResult.token) Object.assign(payload, {
          quantity_review_token: reviewResult.token, quantity_review_acknowledged: true,
        });
      }
      const response = await patchDeliveryNoteLines(payload);
      if (documentScopeRef.current !== deliveryNoteId) return;
      const createdIds = new Map((response.data?.created_lines || []).map((entry) => [Number(entry.index), entry.id]));
      const updatedLines = new Map((response.data?.updated_lines || []).map((line) => [String(line.id), line]));
      const savedLines = payload.lines.map((line, index) => {
        const savedId = line.id || createdIds.get(index);
        const savedLine = { ...line, id: savedId,
          receiving_line_merge: undefined,
          ...(line.inventory_quantity_source === "document" ? { inventory_quantity_snapshot: Number(line.quantity_in_dn) / line.expected_unit_factor } : {}),
          ...updatedLines.get(String(savedId)),
          __persisted_unit_id: line.expected_unit_id, __persisted_inventory_unit_id: line.expected_inventory_unit_id,
        };
        return isCorrection ? savedLine : refreshDeliveryNoteLineUnits(savedLine, itemMap.get(String(line.item_id)),
          { preserveSnapshot: !isDraft && line.inventory_quantity_snapshot != null });
      });
      const missingCreatedIds = savedLines.some((line) => !line.id);
      const savedNote = { ...deliveryNote, status: response.data?.status || deliveryNote.status,
        delivery_note_number: historicalSnapshotRef.current.note.delivery_note_number,
        persisted_status: response.data?.persisted_status ?? deliveryNote.persisted_status,
        correction_status: response.data?.correction_status ?? deliveryNote.correction_status,
        total_amount: response.data?.total_amount ?? payload.total_amount };
      historicalSnapshotRef.current = JSON.parse(JSON.stringify({ note: savedNote, lines: savedLines, expenses: payload.expenses, catalogue: items }));
      editBaselineRef.current = { note: savedNote, lines: savedLines, expenses };
      setLines(savedLines); setDeliveryNote(savedNote);
      setDeletedLineIds([]); setDocumentReloadRequired(missingCreatedIds); setVerifiedEditMode(false);
      setNotice({
        tone: "success",
        text: t("Delivery note updated successfully. Total {total}.", { total: formatMoney(response.data?.total_amount ?? payload.total_amount) }),
      });
      const refreshed = await fetchDeliveryNote({ quiet: true });
      if (!refreshed && missingCreatedIds && documentScopeRef.current === deliveryNoteId) {
        setError("Changes were saved, but the updated lines could not be loaded. Reload the delivery note before editing again.");
      }
      if (!number) window.dispatchEvent(new CustomEvent("refreshDeliveryNotesList"));
    } catch (requestError) {
      if (handleMainLocationError(requestError)) return;
      if (quantityReview.handleError(requestError)) return;
      console.error("[EditDeliveryNotePopup] Error saving delivery note:", requestError);
      setError(deliveryNoteCorrectionError(requestError, languageId) || deliveryNoteNumberError(requestError, languageId) || requestError.response?.data?.error || requestError.message || t("Could not save this delivery note."));
    } finally {
      saveInProgress.current = false;
      setBusyAction("");
    }
  };

  const cancelVerifiedEdit = async () => {
    const snapshot = historicalSnapshotRef.current;
    if (snapshot) {
      setDeliveryNote(snapshot.note); setLines(snapshot.lines); setExpenses(snapshot.expenses); setItems(snapshot.catalogue);
    }
    setVerifiedEditMode(false);
    setDeletedLineIds([]); setItemPicker({ open: false, query: "" }); setNotice(null);
    setError("");
    await fetchDeliveryNote({ quiet: true });
  };

  const verifyDeliveryNote = async ({ sourceLines = lines, headerLocationId = deliveryNote?.destination_location_id } = {}) => {
    if (saveInProgress.current || busyAction || unitRefreshError || documentReloadRequired || isReturn) return;
    if (!sourceLines.length) {
      setError(t("Add at least one item before verifying this delivery note."));
      return;
    }
    if (!requireLineDestinations(sourceLines, headerLocationId)) return;
    saveInProgress.current = true;
    setBusyAction("verify");
    setError("");
    let draftSaved = false;
    try {
      const preparedLines = await confirmComplimentaryLines({}, sourceLines);
      if (!preparedLines) return;
      if (preparedLines.some((line) => !(Number(line.quantity_in_dn) > 0) ||
        !isDeliveryNotePriceValidForVerification(line))) {
        setError(t("Every line needs a positive quantity and a valid price, or an explicit Complimentary or Missing choice."));
        return;
      }
      const payload = buildPayload(preparedLines, headerLocationId);
      setLines(preparedLines);
      const reviewResult = isCorrection ? { allowed: true } : await quantityReview.check({ lines: preparedLines });
      if (!reviewResult.allowed) return;
      await patchDeliveryNoteLines(payload);
      if (documentScopeRef.current !== deliveryNoteId) return;
      draftSaved = true;
      // The merge was committed with the draft save even if verification below
      // needs another review. Do not replay the old source IDs on the next save.
      setLines((previous) => previous.map((line) => ({ ...line, receiving_line_merge: undefined })));
      await axios.post(`/api/purchasing/receivings/delivery-notes/${deliveryNoteId}/verify`, {
        expected_delivery_note_date: deliveryNote?.delivery_note_date || null,
        expected_delivery_note_number: historicalSnapshotRef.current.note.delivery_note_number ?? null,
        ...(reviewResult.token ? { quantity_review_token: reviewResult.token, quantity_review_acknowledged: true } : {}),
      });
      onClose?.({ verified: true, id: deliveryNoteId });
    } catch (requestError) {
      if (handleMainLocationError(requestError)) return;
      if (quantityReview.handleError(requestError)) return;
      console.error("[EditDeliveryNotePopup] Error verifying delivery note:", requestError);
      setError(stockBalanceVerificationError(requestError, sourceLines, inventoryLocations, t, draftSaved) ||
        deliveryNoteCorrectionError(requestError, languageId) || deliveryNoteNumberError(requestError, languageId) ||
        requestError.response?.data?.error || requestError.message || t("Could not verify this delivery note."));
    } finally {
      saveInProgress.current = false;
      setBusyAction("");
    }
  };

  const changeGeneralDestination = (destination) => {
    const pending = pendingVerificationLocationRef.current;
    pendingVerificationLocationRef.current = null;
    setDeliveryNote((previous) => previous ? { ...previous, destination_location_id: destination } : previous);
    // Resume this explicit Verify action with the selected destination, without
    // waiting for React's state updates. Invalid Main is unresolved, even when
    // saved as a manual choice; preserve only valid per-line overrides.
    const sourceLines = lines.map((line) => line.item_id && (mainIsUnavailable(editedLineDestination(line))
      || (pending && (!line.destination_location_id || ["suggested", "inherited", "header"].includes(line.destination_location_source))))
      ? { ...line, destination_location_id: destination, destination_location_source: "header" } : line);
    setLines(sourceLines);
    if (locationRequestMessage) { setError(""); setLocationRequestMessage(""); }
    if (!pending || !destination || !isOpen || !isDraft || pending.deliveryNoteId !== deliveryNoteId
      || pending.businessUnitId !== activeBusinessUnitId) return;
    void verifyDeliveryNote({ sourceLines, headerLocationId: destination });
  };

  const reopenDeliveryNote = async () => {
    setBusyAction("reopen");
    setError("");
    try {
      const { catalogue, refreshed } = await loadCurrentUnitLines();
      const visibleExpenses = JSON.parse(JSON.stringify(expenses));
      if (documentScopeRef.current !== deliveryNoteId) return;
      await axios.patch(`/api/purchasing/receivings/delivery-notes/${deliveryNoteId}/revert`);
      if (documentScopeRef.current !== deliveryNoteId) return;
      setDeliveryNote((previous) => (previous ? { ...previous, status: "draft" } : previous));
      setItems(catalogue); setUnitRefreshError("");
      setLines(refreshed);
      setExpenses(visibleExpenses);
      setNotice({
        tone: "warning",
        text: "Delivery note reopened in draft mode. The current values remain visible locally until you save again.",
      });
      window.dispatchEvent(new CustomEvent("refreshDeliveryNotesList"));
    } catch (requestError) {
      console.error("[EditDeliveryNotePopup] Error reopening delivery note:", requestError);
      setError(t(requestError.response ? "Could not reopen this delivery note." : requestError.message));
    } finally {
      setBusyAction("");
    }
  };

  const deleteDeliveryNote = async () => {
    setBusyAction("delete");
    setError("");
    try {
      await axios.delete(`/api/purchasing/delivery-notes-direct/${deliveryNoteId}`);
      onClose?.({ deleted: true, id: deliveryNoteId });
    } catch (requestError) {
      console.error("[EditDeliveryNotePopup] Error deleting delivery note:", requestError);
      setError(requestError.response?.data?.error || t("Could not delete this delivery note. It may no longer be in draft status."));
      setBusyAction("");
    }
  };

  const cancelDeliveryNote = async () => {
    if (!canCancel || !cancellationReady || busyAction || saveInProgress.current) return;
    saveInProgress.current = true; setBusyAction("cancel"); setError("");
    try {
      const cancellationLocations = cancellationPreview.lines.filter((line) => Number(line.stock_quantity_to_remove) > 0)
        .map((line) => ({ line_id: line.line_id, location_id: cancellationPreview.selectedLocations[line.line_id] }));
      await axios.post(`/api/purchasing/receivings/delivery-notes/${deliveryNoteId}/cancel`, { reason: cancellationReason.trim() || null, cancellation_locations: cancellationLocations });
      if (documentScopeRef.current !== deliveryNoteId) return;
      const snapshot = historicalSnapshotRef.current;
      if (snapshot) {
        setDeliveryNote({ ...snapshot.note, status: "cancelled" }); setLines(snapshot.lines); setExpenses(snapshot.expenses);
      } else setDeliveryNote((previous) => ({ ...previous, status: "cancelled" }));
      setVerifiedEditMode(false); setDeletedLineIds([]); setItemPicker({ open: false, query: "" }); setNotice(null);
      window.dispatchEvent(new CustomEvent("refreshDeliveryNotesList"));
    } catch (requestError) {
      if (documentScopeRef.current === deliveryNoteId) setError((requestError.response?.data?.code === "DELIVERY_NOTE_EXCESS_LATER_CONSUMPTION" ? t(cancellationBlockingMessage(requestError.response.data)) : requestError.response?.data?.error) || t(Number(requestError.response?.status) >= 400 && Number(requestError.response?.status) < 500
        ? "Could not cancel this delivery note. No changes were applied."
        : "Could not confirm cancellation. Reload the delivery note to check its status."));
    } finally {
      saveInProgress.current = false;
      if (documentScopeRef.current === deliveryNoteId) setBusyAction("");
    }
  };

  const runConfirmedAction = async () => {
    const type = confirmState.type;
    setConfirmState({ open: false, type: "" });
    if (type === "verify") {
      await verifyDeliveryNote();
      return;
    }
    if (type === "reopen") {
      await reopenDeliveryNote();
      return;
    }
    if (type === "delete") await deleteDeliveryNote();
    if (type === "cancel") await cancelDeliveryNote();
  };

  const handleOpenOriginalCopy = () => {
    const ocrImportId = deliveryNote?.source_ocr_import?.id || deliveryNote?.sourceOcrImport?.id;
    if (!ocrImportId) return;
    originalViewer.open(ocrImportId, deliveryNote.source_ocr_import?.original_file_name || deliveryNote.sourceOcrImport?.original_file_name);
  };

  if (!isOpen) return null;

  const blockWhileSaving = (event) => {
    if (saveInProgress.current || complimentary.isOpen || busyAction) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <>
    <div className="edn-wrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
      aria-hidden={complimentary.isOpen || undefined}
      onClickCapture={blockWhileSaving} onChangeCapture={blockWhileSaving} onKeyDownCapture={blockWhileSaving}>
      <style>{styles}</style>
      <OriginalDocumentLayout original={originalViewer.original} onClose={originalViewer.close}>
      <div className="edn-modal" role="dialog" aria-modal={!originalViewer.original} aria-label={t("Delivery Note")}>
        <div className="edn-head">
          <div>
            <h2 className="edn-title"> {t("Delivery Note")} {deliveryNote?.delivery_note_number || (deliveryNote ? `#${deliveryNote.id}` : "")}
            </h2>
            <div className="edn-sub">
              {deliveryNote
                ? `${deliveryNote.supplier?.name || t("Unknown supplier")} · ${formatDate(deliveryNote.delivery_note_date)}`
                : t("Review and confirm supplier delivery note")}
            </div>
          </div>

          <div className="edn-headActions">
            {(isReturn || isCorrection) && <span className="edn-status">{ct(isReturn ? "Return" : "Rectification")}</span>}
            {deliveryNote ? (
              <span className={getStatusTone(deliveryNote.status)}>
                {t(statusLabels[deliveryNote.status] || deliveryNote.status)}
              </span>
            ) : null}
            {deliveryNote?.source_ocr_import?.id || deliveryNote?.sourceOcrImport?.id ? (
              <button
                type="button"
                className="edn-btn edn-btn--original"
                onClick={handleOpenOriginalCopy}
                title={
                  deliveryNote.source_ocr_import?.original_file_name ||
                  deliveryNote.sourceOcrImport?.original_file_name ||
                  t("Open original delivery note copy")
                }
              >
                {t("Original")}
              </button>
            ) : null}
            <button type="button" className="edn-close" onClick={() => onClose?.()} disabled={Boolean(busyAction) || receiveModal.busy || complimentary.isOpen}>
              {isMobile ? t("Close") : t("X")}
            </button>
          </div>
        </div>

        <div className="edn-body">
          {deliveryNote?.status === "cancelled" && <div className="edn-banner edn-banner--warning" role="status">{t("This delivery note has been cancelled. Its posted movements were reversed; the document is read-only.")} {t("Cancelled delivery notes are excluded from price histories and statistics and kept for audit.")}</div>}
          {error ? <div className="edn-banner edn-banner--error" role="alert">{t(error)}</div> : null}
          {documentReloadRequired && <button type="button" className="edn-btn edn-btn--soft" onClick={() => fetchDeliveryNote()} disabled={Boolean(busyAction) || loading}>{t("Reload delivery note")}</button>}
          {unitRefreshError ? <div className="edn-banner edn-banner--error" role="alert">
            {t(unitRefreshError)} <button type="button" className="edn-btn edn-btn--soft" onClick={startEditing} disabled={Boolean(busyAction)}>{t("Refresh item units")}</button>
          </div> : null}
          {notice ? (
            <div className={`edn-banner ${notice.tone === "warning" ? "edn-banner--warning" : "edn-banner--success"}`}>
              {t(notice.text)}
            </div>
          ) : null}

          {loading ? (
            <div className="edn-empty">
              <strong>{t("Loading delivery note")}</strong>
              <span>{t("The lines, differences and expenses are being prepared.")}</span>
            </div>
          ) : !deliveryNote ? (
            <div className="edn-empty">
              <strong>{t("Delivery note not found")}</strong>
              <span>{t("This document could not be loaded or no longer exists.")}</span>
            </div>
          ) : (
            <>
              <div className="edn-grid edn-grid--top">
                <section className="edn-section">
                  <div className="edn-grid edn-grid--four">
                    <div>
                      <span className="edn-label">{t("Supplier")}</span>
                      <span className="edn-value">{deliveryNote.supplier?.name || "-"}</span>
                      {deliveryNote.distributor && <div>
                        <span className="edn-label">{ocrDistributorTranslator(languageId)("Distributor")}</span>
                        <span className="edn-value">{deliveryNote.distributor.name}</span>
                      </div>}
                    </div>
                    <div>
                      <label className="edn-label" htmlFor={`edn-delivery-note-number-${deliveryNote.id}`}>{t("Delivery Note Number")}</label>
                      <input id={`edn-delivery-note-number-${deliveryNote.id}`} type="text" className="form-control"
                        maxLength={100} value={deliveryNote.delivery_note_number ?? ""}
                        disabled={!canEditNumber || Boolean(busyAction) || receiveModal.busy}
                        aria-describedby={deliveryNote.status === "invoiced" ? "edn-invoiced-number-help" : undefined}
                        onChange={(event) => setDeliveryNote({ ...deliveryNote, delivery_note_number: event.target.value })} />
                      {deliveryNote.status === "invoiced" && <div className="edn-note" id="edn-invoiced-number-help">
                        {t("Reopen the reconciled invoice before changing this delivery note number.")}
                      </div>}
                    </div>
                    <div>
                      <span className="edn-label">{deliveryNoteCorrectionText(languageId, "Delivery note type")}</span>
                      <DeliveryNoteCorrectionControl note={deliveryNote} businessUnitId={activeBusinessUnitId} disabled={!isDraft || !canEditSupplierValues || Boolean(busyAction)}
                        onClear={() => { setDeliveryNote((previous) => ({ ...previous, document_type: "standard", original_delivery_note_id: null, original_delivery_note_number: null })); setLines((previous) => previous.map((line) => ({ ...line, original_delivery_note_line_id: null, correction_direction: null, correction_available_quantity: null, received_quantity: null, inventory_quantity_snapshot: null }))); }}
                        onSelect={(original, selectedLines) => {
                          setExpenses([]);
                          setDeliveryNote((previous) => ({ ...previous, document_type: "rectification", correction_manual_pending: false, original_delivery_note_id: original.id, original_delivery_note_number: original.delivery_note_number }));
                          setLines(selectedLines.map((line) => normalizeLine({ ...line, id: lines.find((existing) => String(existing.original_delivery_note_line_id) === String(line.original_delivery_note_line_id))?.id })));
                        }} />
                    </div>
                    <div>
                      <label className="edn-label" htmlFor={`edn-delivery-note-date-${deliveryNote.id}`}>
                        {t("Delivery Note Date")}
                      </label>
                      <input
                        id={`edn-delivery-note-date-${deliveryNote.id}`}
                        type="date"
                        className="form-control"
                        value={deliveryNote.delivery_note_date || ""}
                        onChange={(event) =>
                          setDeliveryNote((previous) =>
                            previous
                              ? { ...previous, delivery_note_date: event.target.value }
                              : previous
                          )
                        }
                        disabled={(!isDraft && !canEditSupplierValues) || Boolean(busyAction) || receiveModal.busy}
                        aria-describedby={canReopen && verifiedEditMode ? `edn-delivery-note-date-help-${deliveryNote.id}` : undefined}
                        title={
                          isDraft
                            ? t("Delivery note date for this draft")
                            : verifiedEditMode
                              ? t("Changing this date also updates the stock receipt date.")
                              : canReopen ? t("Choose Edit to change the delivery note date") : t("This delivery note date is read-only")
                        }
                      />
                      {canReopen && verifiedEditMode && <div className="edn-note" id={`edn-delivery-note-date-help-${deliveryNote.id}`}>
                        {t("Changing this date also updates the stock receipt date.")}
                      </div>}
                    </div>
                    <div>
                      <span className="edn-label">{t("Destination Location")}</span>
                      <DeliveryNoteLocationPicker
                        requestOpen={locationRequest}
                        requestMessage={locationRequestMessage}
                        selectionRequired={canEditSupplierValues && mainIsUnavailable(deliveryNote.destination_location_id)}
                        value={deliveryNote.destination_location_id || ""} locations={canEditSupplierValues ? generalDestinationLocations : inventoryLocations}
                        resolvedName={locationName(deliveryNote.destination_location_id)}
                        label={t("Destination Location")} disabled={isCorrection || !canEditSupplierValues || Boolean(busyAction) || receiveModal.busy}
                        onChange={changeGeneralDestination}
                        onCancel={() => { pendingVerificationLocationRef.current = null; }}
                      />
                    </div>
                    <div>
                      <span className="edn-label">{t("Initiated By")}</span>
                      <span className="edn-value">{formatDocumentInitiator(deliveryNote)}</span>
                    </div>
                  </div>
                </section>

                <aside className="edn-summary">
                  <h6>{t("Review Summary")}</h6>
                  <div className="edn-stats">
                    <div className="edn-stat">
                      <span>{t("Lines")}</span>
                      <strong>{lines.length}</strong>
                    </div>
                    <div className="edn-stat">
                      <span>{t("Differences")}</span>
                      <strong>{differencesCount}</strong>
                    </div>
                    <div className="edn-stat">
                      <span>{t("Expenses")}</span>
                      <strong>{expenses.length}</strong>
                    </div>
                    <div className="edn-stat">
                      <span>{t("Matched Lines")}</span>
                      <strong>{Math.max(lines.length - differencesCount, 0)}</strong>
                    </div>
                  </div>
                </aside>
              </div>

              <TenderDeliveryNoteWarning isOpen={isOpen} deliveryNoteId={deliveryNote.id} supplierId={deliveryNote.supplier_id} date={deliveryNote.delivery_note_date} currency={deliveryNote.currency} lines={lines} />
              <section className="edn-section mt-3">
                <div className="edn-sectionHead">
                  <div>
                    <h6>{t("Delivery Note Lines")}</h6>
                    <p>{t(showReceivedColumn ? "Edit the note quantities and prices; received values are shown for comparison." : "Edit the note quantities and prices.")}</p>
                  </div>

                  {canEditSupplierValues && !isCorrection ? (
                    <div className="edn-actions">
                      <button type="button" className="edn-btn edn-btn--primary" onClick={addItem} disabled={Boolean(busyAction)}> {t("Add Item")} </button>
                      {isDraft && hasPendingReceivingValues && <button type="button" className="edn-btn edn-btn--cyan" onClick={copyFromReceiving} disabled={Boolean(busyAction) || Boolean(unitRefreshError)}> {t("Copy from Receiving")} </button>}
                    </div>
                  ) : null}
                </div>

                {destinationError && <p className="edn-itemMeta" role="status">{t("Could not load location suggestions. Saved locations are preserved.")}</p>}

                {!lines.length ? (
                  <div className="edn-empty">
                    <strong>{t("No items in this delivery note")}</strong>
                    <span>{isDraft ? t("Use Add Item to build the draft before verifying it.") : t("There are no lines associated with this document.")}</span>
                  </div>
                ) : isMobile ? (
                  <div className="edn-mobile">
                    {lines.map((line, index) => {
                      const lineTitle = line.item_name || line.description || t("Unnamed item");
                      const pendingTotal = (pendingByItem.get(String(line.item_id)) || []).reduce((sum, candidate) => sum + pendingQtyFromPo(candidate), 0);
                      const unitSource = mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line);
                      const unitOptions = buildDeliveryNoteUnitOptions(unitSource, undefined, languageId).filter((option) => !isCorrection || option.level === "unit" || option.level === line.__persisted_unit_level);
                      const inventoryValues = deliveryNoteInventoryValues(line, unitSource, undefined, languageId);
                      const receivedUnit = inventoryValues.option?.inventoryLabel || t("unit");
                      return (
                        <article key={`delivery-note-line-${line.id || index}`} className="edn-card">
                          <div className="edn-cardTop">
                            <div>
                              <div className="edn-cardTitle">{lineTitle}</div>
                              {lineLocationControl(line, index)}
                              <div className="edn-cardSub">
                                {line.item_code || t("No code")}
                                {showReceivedColumn && <ReceivedValues values={receivedValues[index]} unit={receivedUnit} t={t} showLabel />}
                              </div>
                            </div>
                            <div className="edn-cardActions">
                              {line.difference_flag && <span className="edn-diff edn-diff--warn">{t("Difference")}</span>}
                              {canEditSupplierValues ? (
                                <button type="button" className="edn-btn edn-btn--danger" onClick={() => removeLine(index)} disabled={Boolean(busyAction)}> {t("Remove")} </button>
                              ) : null}
                            </div>
                          </div>

                          <div className="edn-mobileGrid">
                            <div>
                              <label className="edn-label">{t("Unit on Note")}</label>
                              <UnitLevelControl
                                idPrefix={`edn-mobile-uom-${line.id || index}`}
                                options={unitOptions}
                                value={line.unit_level || "unit"}
                                disabled={!canEditSupplierValues}
                                onChange={(unitLevel) => setLineField(index, "unit_level", unitLevel)}
                              />
                              <div className="edn-unitMetaBox">
                                {unitOptions.length > 1 ? <span className="edn-unitRelation">{unitHierarchyLabel(unitSource, undefined, languageId)}</span> : null}
                                <span className="edn-unitPreview">{inventoryUnitSummary(inventoryPreviewLine(line), unitSource, t, languageId, deliveryNote.document_type)}</span>
                              </div>
                            </div>
                            <div>
                              <label className="edn-label" title={t("Qty on Note")}>Q</label>
                              <DeliveryNoteQuantityDetail line={inventoryPreviewLine(line)} source={unitSource} documentType={deliveryNote.document_type}><input type="number" min="0" step="0.00000001" className="form-control edn-noteNumber" value={line.quantity_in_dn ?? ""} onChange={(event) => setLineField(index, "quantity_in_dn", event.target.value)} disabled={!canEditSupplierValues} /></DeliveryNoteQuantityDetail>
                            </div>
                            <div>
                              <label className="edn-label">{t("Price on Note (EUR)")}</label>
                              <ComplimentaryPriceField type="number" min="0" step="0.00000001" className="form-control edn-noteNumber"
                                value={editingPriceIndex === index ? line.unit_price_in_dn ?? "" : displayUnitPrice(line.unit_price_in_dn)} aria-label={t("Unit Price (EUR)")}
                                isComplimentary={Boolean(line.is_free_stock)}
                                priceStatus={line.price_status}
                                priceEstimateSource={line.price_estimate_source}
                                onPriceOptions={() => editLinePrice(index)}
                                onChange={(event) => { setEditingPriceIndex(index); setLineField(index, "unit_price_in_dn", event.target.value); }}
                                onBlur={() => setEditingPriceIndex(null)} disabled={!canEditSupplierValues} />
                            </div>
                            <div>
                              <label className="edn-label">{t("Expiration date")}</label>
                              <ExpirationDateField line={line} item={itemMap.get(String(line.item_id))}
                                disabled={!canEditSupplierValues || Boolean(busyAction)} onChange={(value) => setLineField(index, "expiration_date", value)} />
                            </div>
                          </div>

                          <div className="edn-stat mt-3">
                            <span>{t("Line Total")}</span>
                            <strong>{formatMoney(calculateLineTotal(line))}</strong>
                          </div>
                          <div className="edn-stat mt-3">
                            <span>{t("Pending PO")}</span>
                            <strong>
                              {assignmentQty(line) > 0
                                ? `Received ${formatQty(assignmentQty(line))} ${inventoryValues.option?.inventoryLabel || ""}`
                                : loadingPendingPo && line.item_id
                                  ? t("Checking...")
                                  : pendingTotal > 0
                                    ? `${formatQty(pendingTotal)} pending`
                                    : t("No pending")}
                              <ReceivedPurchaseOrderCheck line={line} label={t("OK")} />
                            </strong>
                            {canReceivePurchaseOrders && !loadingPendingPo && pendingTotal > 0 && line.id && line.item_id ? (
                              <button type="button" className="edn-btn edn-btn--cyan mt-2" onClick={() => openReceiveModal(index)} disabled={Boolean(unitRefreshError) || Boolean(busyAction)}> {t("Receive")} </button>
                            ) : canReceivePurchaseOrders && !loadingPendingPo && pendingTotal > 0 && !line.id ? (
                              <div className="edn-itemMeta">{t("Save this line before linking a pending PO.")}</div>
                            ) : null}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="edn-tableShell">
                    <div className="edn-tableWrap">
                      <table className={`edn-table ${canEditSupplierValues ? "edn-table--draft" : ""} ${showReceivedColumn ? "" : "edn-table--without-received"}`}>
                        <colgroup>
                          <col className="edn-col--item" />
                          <col className="edn-col--unit" />
                          {showReceivedColumn && <col className="edn-col--received" />}
                          <col className="edn-col--qtyNote" />
                          <col className="edn-col--priceNote" />
                          <col className="edn-col--expiration" />
                          <col className="edn-col--pending" />
                          <col className="edn-col--total" />
                          {canEditSupplierValues ? <col className="edn-col--actions" /> : null}
                        </colgroup>
                        <thead>
                          <tr>
                            <th>{t("Item")}</th>
                            <th style={{ textAlign: "center" }}>{t("Unit on Note")}</th>
                            {showReceivedColumn && <th style={{ textAlign: "center" }}>{t("Received")}</th>}
                            <th style={{ textAlign: "center" }} title={t("Qty on Note")}>Q</th>
                            <th style={{ textAlign: "center" }}>{t("Price on Note")}</th>
                            <th>{t("Expiration date")}</th>
                            <th style={{ textAlign: "center" }}>{t("Pending PO")}</th>
                            <th style={{ textAlign: "right" }}>{t("Total")}</th>
                            {canEditSupplierValues ? <th><span className="visually-hidden">{t("Actions")}</span></th> : null}
                          </tr>
                        </thead>
                          <tbody>
                          {lines.map((line, index) => {
                            const pendingCandidates = pendingByItem.get(String(line.item_id)) || [];
                            const pendingTotal = pendingCandidates.reduce((sum, candidate) => sum + pendingQtyFromPo(candidate), 0);
                            const assignedTotal = assignmentQty(line);
                            const unitSource = mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line);
                            const unitOptions = buildDeliveryNoteUnitOptions(unitSource, undefined, languageId).filter((option) => !isCorrection || option.level === "unit" || option.level === line.__persisted_unit_level);
                            const inventoryValues = deliveryNoteInventoryValues(line, unitSource, undefined, languageId);
                            const receivedUnit = inventoryValues.option?.inventoryLabel || t("unit");
                            return (
                              <tr key={`delivery-note-row-${line.id || index}`}>
                                <td className="edn-itemCell">
                                  <div className="edn-itemTitle" title={line.item_name || line.description || t("Unnamed item")}>{line.item_name || line.description || t("Unnamed item")}</div>
                                  <div className="edn-itemMeta">{line.item_code || t("No code available")}</div>
                                  {lineLocationControl(line, index)}
                                </td>
                                <td className="edn-unitCell">
                                  <UnitLevelControl
                                    idPrefix={`edn-uom-${line.id || index}`}
                                    options={unitOptions}
                                    value={line.unit_level || "unit"}
                                    disabled={!canEditSupplierValues}
                                    onChange={(unitLevel) => setLineField(index, "unit_level", unitLevel)}
                                  />
                                  <div className="edn-unitMetaBox">
                                    {unitOptions.length > 1 ? <span className="edn-unitRelation">{unitHierarchyLabel(unitSource, undefined, languageId)}</span> : null}
                                    <span className="edn-unitPreview">{inventoryUnitSummary(inventoryPreviewLine(line), unitSource, t, languageId, deliveryNote.document_type)}</span>
                                  </div>
                                </td>
                                {showReceivedColumn && <td className="edn-readonly">
                                  <ReceivedValues values={receivedValues[index]} unit={receivedUnit} t={t} />
                                </td>}
                                <td>
                                  <DeliveryNoteQuantityDetail line={inventoryPreviewLine(line)} source={unitSource} documentType={deliveryNote.document_type}><input type="number" min="0" step="0.00000001" className="form-control edn-noteNumber" value={line.quantity_in_dn ?? ""} onChange={(event) => setLineField(index, "quantity_in_dn", event.target.value)} disabled={!canEditSupplierValues} /></DeliveryNoteQuantityDetail>
                                </td>
                                <td>
                                  <ComplimentaryPriceField type="number" min="0" step="0.00000001" className="form-control edn-noteNumber"
                                    value={editingPriceIndex === index ? line.unit_price_in_dn ?? "" : displayUnitPrice(line.unit_price_in_dn)} aria-label={t("Unit Price (EUR)")}
                                    isComplimentary={Boolean(line.is_free_stock)}
                                    priceStatus={line.price_status}
                                    priceEstimateSource={line.price_estimate_source}
                                    onPriceOptions={() => editLinePrice(index)}
                                    onChange={(event) => { setEditingPriceIndex(index); setLineField(index, "unit_price_in_dn", event.target.value); }}
                                    onBlur={() => setEditingPriceIndex(null)} disabled={!canEditSupplierValues} />
                                </td>
                                <td><ExpirationDateField line={line} item={itemMap.get(String(line.item_id))}
                                  disabled={!canEditSupplierValues || Boolean(busyAction)} onChange={(value) => setLineField(index, "expiration_date", value)} /></td>
                                <td>
                                  <div className="edn-poStatus">
                                    <span
                                      className={`edn-poText ${
                                        assignedTotal > 0 ? "edn-poText--linked" : pendingTotal > 0 ? "edn-poText--pending" : ""
                                      }`}
                                    >
                                      {canReceivePurchaseOrders && !loadingPendingPo && pendingTotal > 0 && !line.id
                                        ? t("Save first")
                                        : assignedTotal > 0
                                        ? `Received ${formatQty(assignedTotal)} ${inventoryValues.option?.inventoryLabel || ""}`
                                        : loadingPendingPo && line.item_id
                                          ? t("Checking...")
                                          : pendingTotal > 0
                                            ? `${formatQty(pendingTotal)} pending`
                                            : t("No pending")}
                                      <ReceivedPurchaseOrderCheck line={line} label={t("OK")} />
                                    </span>
                                    {canReceivePurchaseOrders && !loadingPendingPo && pendingTotal > 0 && line.id && line.item_id ? (
                                      <button type="button" className="edn-btn edn-btn--cyan" onClick={() => openReceiveModal(index)} disabled={Boolean(unitRefreshError) || Boolean(busyAction)}> {t("Receive")} </button>
                                    ) : null}
                                  </div>
                                </td>
                                <td className="edn-total">
                                  <div>{formatMoney(calculateLineTotal(line))}</div>
                                  {line.difference_flag && <span className="edn-diff edn-diff--warn">{t("Difference")}</span>}
                                </td>
                                {canEditSupplierValues ? (
                                  <td style={{ textAlign: "right" }}>
                                    <button type="button" className="edn-removeLine" onClick={() => removeLine(index)} disabled={Boolean(busyAction)}
                                      aria-label={`${t("Remove Line")}: ${line.item_name || line.description || t("Unnamed item")}`} title={t("Remove Line")}>×</button>
                                  </td>
                                ) : null}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </section>

              <DeliveryNoteAdjustments lines={lines} expenses={expenses} languageId={languageId}
                disabled={isCorrection || !canEditSupplierValues || Boolean(busyAction)} onChange={(nextLines, nextExpenses) => {
                  setLines(nextLines.map(normalizeLine)); setExpenses(nextExpenses); setEditingPriceIndex(null); setError("");
                }} />

              {hasLinkedCorrections && <p className="dnc-help">{ct("Cancel the linked rectifications before editing this original delivery note.")}</p>}
              {isReturn && <p className="dnc-help">{ct("This return has already been dispatched. Its quantities and stock movements cannot be edited here.")}</p>}
              {isCorrection && <p className="dnc-help">{ct(isDraft ? "Rectifications do not change inventory." : "This verified rectification is read-only. Cancel it and create a replacement to change its quantities.")}</p>}
              <DeliveryNotePendingCorrection note={deliveryNote} lines={lines} sources={lines.map((line) => mergeDeliveryNoteUnitSource(itemMap.get(String(line.item_id)), line))} disabled={!canEditSupplierValues || Boolean(busyAction)}
                onChange={(value) => setDeliveryNote((previous) => ({ ...previous, correction_manual_pending: value }))}
                onLineChange={(index, value) => setLineField(index, "correction_pending_quantity", value)} />
              {!isCorrection && !isReturn && quantityReview.review}

              <div className="edn-footer mt-3">
                <div className="edn-totalBlock">
                  <span>{t(hasPendingPrices ? "Provisional total" : "Total")}</span>
                  <strong>{formatMoney(total)}</strong>
                </div>

                <div className="edn-footerActions">
                  {canReopen && !verifiedEditMode && !documentReloadRequired ? (
                    <button type="button" className="edn-btn edn-btn--primary" onClick={startEditing} disabled={Boolean(busyAction)}> {t("Edit")} </button>
                  ) : null}

                  {(canEditSupplierValues || (canEditNumber && hasEditedNumber)) ? (
                    <button type="button" className="edn-btn edn-btn--primary" onClick={saveChanges} disabled={Boolean(busyAction)}>
                      {busyAction === "save" ? t("Saving...") : t("Save Changes")}
                    </button>
                  ) : null}

                  {canReopen && verifiedEditMode ? (
                    <button type="button" className="edn-btn edn-btn--soft" onClick={cancelVerifiedEdit} disabled={Boolean(busyAction)}> {t("Cancel Edit")} </button>
                  ) : null}

                  {canCancel && <button type="button" className="edn-btn edn-btn--danger" onClick={() => { setCancellationReason(""); setCancellationPreview({ status: "idle", lines: [], selectedLocations: {}, queries: {} }); setConfirmState({ open: true, type: "cancel" }); }} disabled={Boolean(busyAction)}>{t("Cancel Delivery Note")}</button>}

                  {isDraft ? (
                    <>
                      <button type="button" className="edn-btn edn-btn--success" onClick={() => { if (requireLineDestinations()) setConfirmState({ open: true, type: "verify" }); }} disabled={Boolean(busyAction) || Boolean(unitRefreshError) || documentReloadRequired}> {t("Verify Delivery Note")} </button>
                      <button type="button" className="edn-btn edn-btn--danger" onClick={() => setConfirmState({ open: true, type: "delete" })} disabled={Boolean(busyAction)}> {t("Delete Delivery Note")} </button>
                    </>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      </OriginalDocumentLayout>
      <ConfirmModal
        open={confirmState.open}
        cancellation={confirmState.type === "cancel"} reason={cancellationReason} onReason={setCancellationReason} hasUnsavedEdits={verifiedEditMode}
        cancellationPreview={cancellationPreview} cancellationReady={cancellationReady}
        cancellationStockShortage={cancellationLocationValidation.shortageLineIds.size > 0}
        onRetryCancellation={() => setCancellationPreviewAttempt((attempt) => attempt + 1)}
        onCancellationLocation={(lineId, locationId) => setCancellationPreview((previous) => ({ ...previous, selectedLocations: { ...previous.selectedLocations, [lineId]: locationId } }))}
        onCancellationSearch={(lineId, query) => setCancellationPreview((previous) => ({ ...previous, queries: { ...previous.queries, [lineId]: query } }))}
        title={confirmState.type === "cancel" ? t("Cancel Delivery Note") : confirmState.type === "verify" ? t("Verify Delivery Note") : confirmState.type === "reopen" ? t("Reopen Delivery Note") : t("Delete Delivery Note")}
        message={confirmState.type === "cancel" && isCorrection ? ct("Cancel this rectification? Its quantities will become pending again on the original delivery note. Inventory will not change.") : confirmState.type === "cancel" ? t("Cancel this delivery note? Its posted stock, value and direct consumption will be reversed, and linked purchase order quantities will be adjusted. The document remains available as cancelled.") : confirmState.type === "verify" ? t("Verify this delivery note and confirm all current quantities and prices?") : confirmState.type === "reopen" ? t("This will reopen the delivery note in draft mode. Continue?") : t("Are you sure you want to delete this delivery note? This action cannot be undone.")}
        busy={Boolean(busyAction)}
        onClose={() => setConfirmState({ open: false, type: "" })}
        onConfirm={runConfirmedAction}
      />

      <ItemPickerModal
        open={canEditSupplierValues && itemPicker.open}
        items={filteredItems}
        query={itemPicker.query}
        supplierId={deliveryNote?.supplier_id}
        supplierName={deliveryNote?.supplier?.name}
        onQuery={(query) => setItemPicker((previous) => ({ ...previous, query }))}
        onClose={() => setItemPicker({ open: false, query: "" })}
        onSelect={selectItem}
      />

      <ReceivePoModal
        open={canReceivePurchaseOrders && receiveModal.open}
        line={receiveModal.lineIndex !== null ? lines[receiveModal.lineIndex] : null}
        candidates={
          receiveModal.lineIndex !== null && lines[receiveModal.lineIndex]
            ? candidatesForLine(lines[receiveModal.lineIndex], pendingByItem)
            : []
        }
        quantities={receiveModal.quantities}
        busy={receiveModal.busy}
        onQuantity={setReceiveQuantity}
        onClose={() => setReceiveModal({ open: false, lineIndex: null, quantities: {}, busy: false })}
        onApply={applyReceiveAssignments}
      />
    </div>
    {complimentary.dialog}
    </>
  );
}
